// Tiny i18n table. Keys are stable; values are user-visible.
// Subscribers (DOM elements that need re-translation on language
// change) register via `onLangChange(fn)`; the active language id
// lives on `state.Language` and gets pushed through `setLang`.

const STRINGS = {
  en: {
    tab_view: 'View', tab_time: 'Time', tab_show: 'Show',
    tab_tracker: 'Tracker', tab_demos: 'Demos', tab_info: 'Info',
    btn_show: 'Show', btn_gp_override: 'GP Override',
    btn_enable_all: 'Enable All', btn_disable_all: 'Disable All',
    btn_disable_satellites: 'Disable Satellites',
    tip_grids: 'Toggle FE grid + Optical-vault grid + heavenly-vault azimuth degrees',
    tip_map: 'Open Map Projection settings',
    tip_starfield: 'Cycle starfield (random / chart dark / chart light / cel nav)',
    tip_vault_swap: 'Swap Heavenly ⇄ Optical Vault',
    tip_rewind: 'Rewind',
    tip_play_pause: 'Play / Pause',
    tip_fast_forward: 'Fast forward',
    tip_slow: 'Slow play speed (halve). Resumes at this speed if paused.',
    tip_speed: 'Speed up play (double). Resumes at this speed if paused.',
    tip_end_demo: 'Stop the currently playing demo',
    tip_clear_follow: 'Clear follow target (Esc also works)',
    tip_permanent_night: 'Toggle permanent night (dark sky with stars)',
    tip_true_positions: 'Toggle true-position markers on the Heavenly vault',
    tip_stm: 'Specified Tracker Mode — focus scene on FollowTarget only',
    tip_tracker_opts_jump: 'Jump to Tracker Options',
    tip_observer_jump: 'Jump to Observer',
    tip_freecam: 'Free-camera mode — arrow keys rotate / tilt the orbit camera instead of moving the observer',
    tip_az_ring: 'Toggle the full compass readout: azimuth ring, ground longitude ring, and Optical-vault grid lines',
    lang_label: 'Language',
    grp_observer: 'Observer', grp_camera: 'Camera',
    grp_vault_of_heavens: 'Vault of the Heavens',
    grp_optical_vault: 'Optical Vault',
    grp_body_vaults: 'Body Vaults', grp_rays: 'Rays',
    grp_cosmology: 'Cosmology', grp_map_projection: 'Map Projection',
    grp_misc: 'Misc', grp_ephemeris: 'Ephemeris',
    grp_starfield: 'Starfield', grp_tracker_options: 'Tracker Options',
    grp_celestial_bodies: 'Celestial Bodies', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Constellations', grp_black_holes: 'Black Holes',
    grp_quasars: 'Quasars', grp_galaxies: 'Galaxies',
    grp_satellites: 'Satellites',
    grp_bright_star_catalog: 'Bright Star Catalog',
    grp_calendar: 'Calendar', grp_autoplay: 'Autoplay',
    grp_heavenly_vault: 'Heavenly Vault', grp_ground_disc: 'Ground / Disc',
    grp_date_time: 'Date / Time',
    grp_language_select: 'Language Select',

    lbl_heavenly_vault: 'Heavenly Vault', lbl_vault_grid: 'Vault Grid',
    lbl_sun_track: 'Sun Track', lbl_moon_track: 'Moon Track',
    lbl_optical_vault: 'Optical Vault',
    lbl_optical_vault_grid: 'Optical Vault Grid',
    lbl_azimuth_ring: 'Azimuth ring',
    lbl_facing_vector: 'Facing Vector / N-S-E-W',
    lbl_celestial_poles: 'Celestial Poles',
    lbl_declination_circles: 'Declination Circles',
    lbl_fe_grid: 'FE Grid', lbl_tropics_polar: 'Tropics / Polar',
    lbl_sun_moon_gp: 'Sun / Moon GP', lbl_longitude_ring: 'Longitude ring',
    lbl_shadow: 'Shadow',
    lbl_vault_rays: 'Vault Rays', lbl_optical_vault_rays: 'Optical Vault Rays',
    lbl_projection_rays: 'Projection Rays', lbl_many_rays: 'Many Rays',
    lbl_axis_mundi: 'Axis Mundi',
    lbl_planets: 'Planets', lbl_dark_background: 'Dark Background',
    lbl_logo: 'Logo',
    lbl_show: 'Show', lbl_gp_override: 'GP Override',
    lbl_outlines: 'Outlines',
    lbl_figure: 'Figure',
    lbl_starfield: 'Starfield', lbl_starfield_mode: 'Starfield Mode',
    lbl_permanent_night: 'Permanent night',
    lbl_show_satellites: 'Show Satellites',
    lbl_specified_tracker_mode: 'Specified Tracker Mode',
    lbl_true_positions: 'True Positions',
    lbl_gp_path_24h: 'GP Path (24 h)',
    lbl_ephemeris_comparison: 'Ephemeris comparison',
    lbl_precession: 'Precession', lbl_nutation: 'Nutation',
    lbl_aberration: 'Aberration', lbl_trepidation: 'Trepidation',
    lbl_source: 'Source',
    lbl_observer_lat: 'ObserverLat', lbl_observer_long: 'ObserverLong',
    lbl_size: 'Size', lbl_height: 'Height',
    lbl_hq_map_art: 'HQ Map Art', lbl_generated: 'Generated',
    lbl_camera_dir: 'CameraDir', lbl_camera_height: 'CameraHeight',
    lbl_camera_dist: 'CameraDist', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevation',
    lbl_vault_size: 'VaultSize', lbl_vault_height: 'VaultHeight',
    lbl_day_of_year: 'DayOfYear', lbl_time: 'Time', lbl_datetime: 'DateTime',
    lbl_timezone: 'Timezone', lbl_date_time_field: 'Date / time',
    lbl_speed: 'Speed',
    btn_pause: 'Pause', btn_play: 'Play',
    status_running: 'running', status_paused: 'paused',
    btn_day: 'Day', btn_week: 'Week', btn_month: 'Month', btn_year: 'Year',
    app_title: 'FE CONCEPTUAL MODEL',
    app_subtitle: 'Modular port. All distances are unitless (FE radius = 1).',
    panel_live_moon_phases: 'Live Moon Phases',
    panel_live_ephemeris_data: 'Live Ephemeris Data',
    beyond_vault: "beyond observer's optical vault",
    within_vault: "within observer's optical vault",
    twilight_civil: 'civil twilight',
    twilight_nautical: 'nautical twilight',
    twilight_astronomical: 'astronomical twilight',
    daylight: 'daylight',
    night: 'night',
    sun_never_leaves: '(sun never leaves optical vault)',
    sun_never_enters: '(sun never enters optical vault)',
    next_solar_eclipse: 'Next solar eclipse',
    next_lunar_eclipse: 'Next lunar eclipse',
    about_btn: 'About', legend_btn: 'Legend',
    about_p1: "This is a conceptual model. The positions of the stars are based purely on kinematics; the observer's optical vault defines the limit of vision onto which the starfield is projected.",
    about_p2: 'Any conceptual model that uses the lat/long graticule is no better or worse than any other projection. The graticule is internally consistent and self-referential to a fictitious observer at the center of the earth — it is that fictitious observer that binds the 60 NMi / 1° relationship to the ground positions of the stars.',
    about_p3: '"Fictitious observer" is not mutually exclusive with the conceptual model: every such model is ultimately grounded in what a single real observer sees in their own hemisphere of vision, and the graticule is simply one bookkeeping convention on top of that.',
    about_p4: 'Tang sidereal mapping. The Chinese-units readouts use the Tang convention that a full sky rotation is 365.25 du, one du per day of the sun\'s drift along the ecliptic. So 1° = 365.25 / 360 ≈ 1.01458 du and 1 du ≈ 0.98563°. Each du divides into 10 fen; the popup shows decimal du for readability.',
    about_p5: 'Distances. Ground lengths are anchored to Yi Xing\'s 8th-century field measurement of 351.267 li per du along a meridian. Multiplied through, the full meridian is 365.25 × 351.267 ≈ 128,300 li ≈ 40,008 km, giving a Tang sphere radius R = 128,300 / 2π ≈ 20,419.45 li. Every li / bu readout (haversine, dot-product, or arc-length R · θ) uses that same R, so degrees, du, fen, li, bu, and km all trace back to a single calibration.',
  },
  cs: {
    tab_view: 'Pohled', tab_time: 'Čas', tab_show: 'Zobrazit',
    tab_tracker: 'Sledovač', tab_demos: 'Ukázky', tab_info: 'Info',
    btn_show: 'Zobrazit', btn_gp_override: 'Přepis GP',
    btn_enable_all: 'Povolit vše', btn_disable_all: 'Zakázat vše',
    btn_disable_satellites: 'Zakázat satelity',
    tip_grids: 'Přepnout FE mřížku + mřížku optické klenby + azimutové stupně nebeské klenby',
    tip_map: 'Otevřít nastavení mapové projekce',
    tip_starfield: 'Procházet hvězdná pole (náhodné / mapa tmavá / mapa světlá / cel nav)',
    tip_vault_swap: 'Přepnout nebeskou ⇄ optickou klenbu',
    tip_rewind: 'Zpět',
    tip_play_pause: 'Přehrát / Pauza',
    tip_fast_forward: 'Rychle vpřed',
    tip_slow: 'Zpomalit přehrávání (poloviční). Při pauze pokračuje touto rychlostí.',
    tip_speed: 'Zrychlit přehrávání (dvojnásobek). Při pauze pokračuje touto rychlostí.',
    tip_end_demo: 'Zastavit aktuální ukázku',
    tip_clear_follow: 'Zrušit sledování cíle (funguje i Esc)',
    tip_permanent_night: 'Trvalá noc (tmavá obloha se hvězdami)',
    tip_true_positions: 'Přepnout značky skutečných pozic na nebeské klenbě',
    tip_stm: 'Režim určeného sledovače — soustředit scénu pouze na cíl',
    tip_tracker_opts_jump: 'Přejít na možnosti sledovače',
    tip_observer_jump: 'Přejít na pozorovatele',
    tip_freecam: 'Volná kamera — šipky otáčejí / naklánějí kameru místo pozorovatele',
    tip_az_ring: 'Přepnout úplný kompasový údaj: azimutový kruh, kruh délky a mřížku optické klenby',
    lang_label: 'Jazyk',
    grp_observer: 'Pozorovatel', grp_camera: 'Kamera',
    grp_vault_of_heavens: 'Nebeská klenba',
    grp_optical_vault: 'Optická klenba',
    grp_body_vaults: 'Klenby těles', grp_rays: 'Paprsky',
    grp_cosmology: 'Kosmologie', grp_map_projection: 'Projekce mapy',
    grp_misc: 'Různé', grp_ephemeris: 'Efemeridy',
    grp_starfield: 'Hvězdné pole', grp_tracker_options: 'Možnosti sledovače',
    grp_celestial_bodies: 'Nebeská tělesa', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Souhvězdí', grp_black_holes: 'Černé díry',
    grp_quasars: 'Kvazary', grp_galaxies: 'Galaxie',
    grp_satellites: 'Satelity',
    grp_bright_star_catalog: 'Katalog jasných hvězd',
    grp_calendar: 'Kalendář', grp_autoplay: 'Automatické přehrávání',
    grp_heavenly_vault: 'Nebeská klenba', grp_ground_disc: 'Země / Disk',
    grp_date_time: 'Datum / Čas',
    grp_language_select: 'Výběr jazyka',

    lbl_heavenly_vault: 'Nebeská klenba', lbl_vault_grid: 'Mřížka klenby',
    lbl_sun_track: 'Stopa Slunce', lbl_moon_track: 'Stopa Měsíce',
    lbl_optical_vault: 'Optická klenba',
    lbl_optical_vault_grid: 'Mřížka optické klenby',
    lbl_azimuth_ring: 'Azimutový kruh',
    lbl_facing_vector: 'Vektor směru / S-J-V-Z',
    lbl_celestial_poles: 'Nebeské póly',
    lbl_declination_circles: 'Deklinační kruhy',
    lbl_fe_grid: 'FE mřížka', lbl_tropics_polar: 'Obratníky / polární',
    lbl_sun_moon_gp: 'GP Slunce / Měsíce', lbl_longitude_ring: 'Kruh délky',
    lbl_shadow: 'Stín',
    lbl_vault_rays: 'Paprsky klenby', lbl_optical_vault_rays: 'Paprsky optické klenby',
    lbl_projection_rays: 'Paprsky projekce', lbl_many_rays: 'Mnoho paprsků',
    lbl_axis_mundi: 'Axis Mundi',
    lbl_planets: 'Planety', lbl_dark_background: 'Tmavé pozadí',
    lbl_logo: 'Logo',
    lbl_show: 'Zobrazit', lbl_gp_override: 'Přepis GP',
    lbl_outlines: 'Obrysy',
    lbl_figure: 'Postava',
    lbl_starfield: 'Hvězdné pole', lbl_starfield_mode: 'Režim pole',
    lbl_permanent_night: 'Trvalá noc',
    lbl_show_satellites: 'Zobrazit satelity',
    lbl_specified_tracker_mode: 'Režim určeného sledovače',
    lbl_true_positions: 'Skutečné pozice',
    lbl_gp_path_24h: 'Trasa GP (24 h)',
    lbl_ephemeris_comparison: 'Srovnání efemerid',
    lbl_precession: 'Precese', lbl_nutation: 'Nutace',
    lbl_aberration: 'Aberace', lbl_trepidation: 'Trepidace',
    lbl_source: 'Zdroj',
    lbl_observer_lat: 'Šířka pozorov.', lbl_observer_long: 'Délka pozorov.',
    lbl_size: 'Velikost', lbl_height: 'Výška',
    lbl_hq_map_art: 'HQ Mapa', lbl_generated: 'Generované',
    lbl_camera_dir: 'Směr kamery', lbl_camera_height: 'Výška kamery',
    lbl_camera_dist: 'Vzdál. kamery', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevace',
    lbl_vault_size: 'Velikost klenby', lbl_vault_height: 'Výška klenby',
    lbl_day_of_year: 'Den roku', lbl_time: 'Čas', lbl_datetime: 'Datum-čas',
    lbl_timezone: 'Časové pásmo', lbl_date_time_field: 'Datum / čas',
    lbl_speed: 'Rychlost',
    btn_pause: 'Pauza', btn_play: 'Přehrát',
    status_running: 'běží', status_paused: 'pozastaveno',
    btn_day: 'Den', btn_week: 'Týden', btn_month: 'Měsíc', btn_year: 'Rok',
    app_title: 'KONCEPČNÍ MODEL FE',
    app_subtitle: 'Modulární port. Všechny vzdálenosti jsou bezrozměrné (FE poloměr = 1).',
    panel_live_moon_phases: 'Živé fáze Měsíce',
    panel_live_ephemeris_data: 'Živá data efemerid',
    beyond_vault: 'mimo optickou klenbu pozorovatele',
    within_vault: 'uvnitř optické klenby pozorovatele',
    twilight_civil: 'občanský soumrak',
    twilight_nautical: 'námořní soumrak',
    twilight_astronomical: 'astronomický soumrak',
    daylight: 'denní světlo',
    night: 'noc',
    sun_never_leaves: '(slunce neopouští optickou klenbu)',
    sun_never_enters: '(slunce nevstupuje do optické klenby)',
    next_solar_eclipse: 'Další zatmění Slunce',
    next_lunar_eclipse: 'Další zatmění Měsíce',
    about_btn: 'O modelu', legend_btn: 'Legenda',
    about_p1: 'Toto je koncepční model. Pozice hvězd jsou založeny čistě na kinematice; optická klenba pozorovatele definuje hranici viditelnosti, na kterou se promítá hvězdné pole.',
    about_p2: 'Žádný koncepční model používající mřížku zeměp. šířky/délky není lepší ani horší než jakákoli jiná projekce. Mřížka je vnitřně konzistentní a samoreferenční vůči fiktivnímu pozorovateli ve středu Země — právě tento fiktivní pozorovatel váže vztah 60 NMi / 1° na pozice hvězd na zemi.',
    about_p3: '„Fiktivní pozorovatel" se nevylučuje s koncepčním modelem: každý takový model je nakonec zakotven v tom, co jeden skutečný pozorovatel vidí ve své vlastní hemisféře vidění, a mřížka je pouze jedna účetní konvence nad tím.',
  },
  es: {
    tab_view: 'Vista', tab_time: 'Tiempo', tab_show: 'Mostrar',
    tab_tracker: 'Rastreador', tab_demos: 'Demos', tab_info: 'Info',
    btn_show: 'Mostrar', btn_gp_override: 'Anular GP',
    btn_enable_all: 'Activar todo', btn_disable_all: 'Desactivar todo',
    btn_disable_satellites: 'Desactivar satélites',
    tip_grids: 'Alternar cuadrícula FE + cuadrícula óptica + grados azimutales del cielo',
    tip_map: 'Abrir ajustes de proyección de mapa',
    tip_starfield: 'Cambiar campo de estrellas (aleatorio / carta oscura / carta clara / cel nav)',
    tip_vault_swap: 'Alternar Bóveda Celeste ⇄ Óptica',
    tip_rewind: 'Retroceder',
    tip_play_pause: 'Reproducir / Pausar',
    tip_fast_forward: 'Avance rápido',
    tip_slow: 'Reducir velocidad (mitad). Al reanudar usa esta velocidad.',
    tip_speed: 'Aumentar velocidad (doble). Al reanudar usa esta velocidad.',
    tip_end_demo: 'Detener la demo actual',
    tip_clear_follow: 'Quitar objetivo de seguimiento (también Esc)',
    tip_permanent_night: 'Noche permanente (cielo oscuro con estrellas)',
    tip_true_positions: 'Alternar marcas de posiciones reales en la bóveda',
    tip_stm: 'Rastreador específico — enfocar la escena solo en el objetivo',
    tip_tracker_opts_jump: 'Ir a Opciones del Rastreador',
    tip_observer_jump: 'Ir a Observador',
    tip_freecam: 'Cámara libre — flechas rotan / inclinan la cámara en vez del observador',
    tip_az_ring: 'Alternar la lectura completa del compás: anillo azimutal, longitud y cuadrícula óptica',
    lang_label: 'Idioma',
    grp_observer: 'Observador', grp_camera: 'Cámara',
    grp_vault_of_heavens: 'Bóveda Celeste',
    grp_optical_vault: 'Bóveda Óptica',
    grp_body_vaults: 'Bóvedas de Cuerpos', grp_rays: 'Rayos',
    grp_cosmology: 'Cosmología', grp_map_projection: 'Proyección de Mapa',
    grp_misc: 'Misc', grp_ephemeris: 'Efemérides',
    grp_starfield: 'Campo de Estrellas', grp_tracker_options: 'Opciones del Rastreador',
    grp_celestial_bodies: 'Cuerpos Celestes', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Constelaciones', grp_black_holes: 'Agujeros Negros',
    grp_quasars: 'Cuásares', grp_galaxies: 'Galaxias',
    grp_satellites: 'Satélites',
    grp_bright_star_catalog: 'Catálogo de Estrellas Brillantes',
    grp_calendar: 'Calendario', grp_autoplay: 'Reproducción Automática',
    grp_heavenly_vault: 'Bóveda Celeste', grp_ground_disc: 'Suelo / Disco',
    grp_date_time: 'Fecha / Hora',
    grp_language_select: 'Selección de idioma',

    lbl_heavenly_vault: 'Bóveda Celeste', lbl_vault_grid: 'Cuadrícula',
    lbl_sun_track: 'Trayectoria Sol', lbl_moon_track: 'Trayectoria Luna',
    lbl_optical_vault: 'Bóveda Óptica',
    lbl_optical_vault_grid: 'Cuadrícula Óptica',
    lbl_azimuth_ring: 'Anillo Azimutal',
    lbl_facing_vector: 'Vector / N-S-E-O',
    lbl_celestial_poles: 'Polos Celestes',
    lbl_declination_circles: 'Círculos Decl.',
    lbl_fe_grid: 'Cuadrícula FE', lbl_tropics_polar: 'Trópicos / Polar',
    lbl_sun_moon_gp: 'GP Sol / Luna', lbl_longitude_ring: 'Anillo Longitud',
    lbl_shadow: 'Sombra',
    lbl_vault_rays: 'Rayos Bóveda', lbl_optical_vault_rays: 'Rayos Óptica',
    lbl_projection_rays: 'Rayos Proyección', lbl_many_rays: 'Muchos Rayos',
    lbl_axis_mundi: 'Axis Mundi',
    lbl_planets: 'Planetas', lbl_dark_background: 'Fondo Oscuro',
    lbl_logo: 'Logo',
    lbl_show: 'Mostrar', lbl_gp_override: 'Anular GP',
    lbl_outlines: 'Contornos',
    lbl_figure: 'Figura',
    lbl_starfield: 'Campo Estrellas', lbl_starfield_mode: 'Modo',
    lbl_permanent_night: 'Noche perm.',
    lbl_show_satellites: 'Mostrar satélites',
    lbl_specified_tracker_mode: 'Rastreador específico',
    lbl_true_positions: 'Pos. reales',
    lbl_gp_path_24h: 'Trayec. GP (24 h)',
    lbl_ephemeris_comparison: 'Comparar efemérides',
    lbl_precession: 'Precesión', lbl_nutation: 'Nutación',
    lbl_aberration: 'Aberración', lbl_trepidation: 'Trepidación',
    lbl_source: 'Fuente',
    lbl_observer_lat: 'Lat obs.', lbl_observer_long: 'Long obs.',
    lbl_size: 'Tamaño', lbl_height: 'Altura',
    lbl_hq_map_art: 'Mapa HQ', lbl_generated: 'Generado',
    lbl_camera_dir: 'Dirección', lbl_camera_height: 'Altura cámara',
    lbl_camera_dist: 'Distancia', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevación',
    lbl_vault_size: 'Tamaño bóveda', lbl_vault_height: 'Altura bóveda',
    lbl_day_of_year: 'Día del año', lbl_time: 'Hora', lbl_datetime: 'Fecha-hora',
    lbl_timezone: 'Zona horaria', lbl_date_time_field: 'Fecha / hora',
    lbl_speed: 'Velocidad',
    btn_pause: 'Pausa', btn_play: 'Reproducir',
    status_running: 'corriendo', status_paused: 'pausado',
    btn_day: 'Día', btn_week: 'Semana', btn_month: 'Mes', btn_year: 'Año',
    app_title: 'MODELO CONCEPTUAL FE',
    app_subtitle: 'Puerto modular. Todas las distancias son adimensionales (radio FE = 1).',
    panel_live_moon_phases: 'Fases Lunares en Vivo',
    panel_live_ephemeris_data: 'Datos de Efemérides en Vivo',
    beyond_vault: 'fuera de la bóveda óptica del observador',
    within_vault: 'dentro de la bóveda óptica del observador',
    twilight_civil: 'crepúsculo civil',
    twilight_nautical: 'crepúsculo náutico',
    twilight_astronomical: 'crepúsculo astronómico',
    daylight: 'luz diurna',
    night: 'noche',
    sun_never_leaves: '(el Sol no abandona la bóveda óptica)',
    sun_never_enters: '(el Sol no entra en la bóveda óptica)',
    next_solar_eclipse: 'Próximo eclipse solar',
    next_lunar_eclipse: 'Próximo eclipse lunar',
    about_btn: 'Acerca', legend_btn: 'Leyenda',
    about_p1: 'Este es un modelo conceptual. Las posiciones de las estrellas se basan puramente en la cinemática; la bóveda óptica del observador define el límite de visión sobre el que se proyecta el campo de estrellas.',
    about_p2: 'Cualquier modelo conceptual que utilice la cuadrícula lat/long no es ni mejor ni peor que cualquier otra proyección. La cuadrícula es internamente coherente y autorreferencial a un observador ficticio en el centro de la Tierra — es ese observador ficticio quien vincula la relación 60 NMi / 1° a las posiciones terrestres de las estrellas.',
    about_p3: '"Observador ficticio" no es mutuamente excluyente con el modelo conceptual: cada modelo de este tipo se basa, en última instancia, en lo que un único observador real ve en su propio hemisferio de visión, y la cuadrícula es simplemente una convención de contabilidad sobre eso.',
  },

  fr: {
    tab_view: 'Vue', tab_time: 'Temps', tab_show: 'Afficher',
    tab_tracker: 'Suiveur', tab_demos: 'Démos', tab_info: 'Info',
    btn_show: 'Afficher', btn_gp_override: 'Forcer GP',
    btn_enable_all: 'Tout activer', btn_disable_all: 'Tout désactiver',
    btn_disable_satellites: 'Désactiver satellites',
    tip_grids: 'Basculer grille FE + grille de la voûte optique + degrés azimutaux',
    tip_map: 'Ouvrir les paramètres de projection',
    tip_starfield: 'Changer de champ d’étoiles',
    tip_vault_swap: 'Permuter Voûte Céleste ⇄ Optique',
    tip_rewind: 'Retour', tip_play_pause: 'Lecture / Pause',
    tip_fast_forward: 'Avance rapide',
    tip_slow: 'Ralentir (moitié). Reprend à cette vitesse si en pause.',
    tip_speed: 'Accélérer (double). Reprend à cette vitesse si en pause.',
    tip_end_demo: 'Arrêter la démo en cours',
    tip_clear_follow: 'Effacer la cible (Esc fonctionne aussi)',
    tip_permanent_night: 'Nuit permanente (ciel sombre étoilé)',
    tip_true_positions: 'Marqueurs de positions vraies sur la voûte céleste',
    tip_stm: 'Mode suiveur spécifié — focaliser sur la cible',
    tip_tracker_opts_jump: 'Aller aux options du suiveur',
    tip_observer_jump: 'Aller à l’observateur',
    tip_freecam: 'Caméra libre — flèches font tourner la caméra',
    tip_az_ring: 'Basculer la lecture complète du compas',
    lang_label: 'Langue',
    grp_observer: 'Observateur', grp_camera: 'Caméra',
    grp_vault_of_heavens: 'Voûte Céleste', grp_optical_vault: 'Voûte Optique',
    grp_body_vaults: 'Voûtes des Corps', grp_rays: 'Rayons',
    grp_cosmology: 'Cosmologie', grp_map_projection: 'Projection',
    grp_misc: 'Divers', grp_ephemeris: 'Éphémérides',
    grp_starfield: 'Champ d’étoiles', grp_tracker_options: 'Options du suiveur',
    grp_celestial_bodies: 'Corps Célestes', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Constellations', grp_black_holes: 'Trous Noirs',
    grp_quasars: 'Quasars', grp_galaxies: 'Galaxies',
    grp_satellites: 'Satellites', grp_bright_star_catalog: 'Catalogue d’étoiles brillantes',
    grp_calendar: 'Calendrier', grp_autoplay: 'Lecture auto',
    grp_heavenly_vault: 'Voûte Céleste', grp_ground_disc: 'Sol / Disque',
    grp_date_time: 'Date / Heure',
    lbl_heavenly_vault: 'Voûte Céleste', lbl_vault_grid: 'Grille',
    lbl_sun_track: 'Trajet Soleil', lbl_moon_track: 'Trajet Lune',
    lbl_optical_vault: 'Voûte Optique', lbl_optical_vault_grid: 'Grille Optique',
    lbl_azimuth_ring: 'Anneau azimut', lbl_facing_vector: 'Vecteur / N-S-E-O',
    lbl_celestial_poles: 'Pôles célestes', lbl_declination_circles: 'Cercles décl.',
    lbl_fe_grid: 'Grille FE', lbl_tropics_polar: 'Tropiques / polaires',
    lbl_sun_moon_gp: 'GP Soleil / Lune', lbl_longitude_ring: 'Anneau longitude',
    lbl_shadow: 'Ombre',
    lbl_vault_rays: 'Rayons Voûte', lbl_optical_vault_rays: 'Rayons Optique',
    lbl_projection_rays: 'Rayons Projection', lbl_many_rays: 'Nombreux rayons',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Planètes',
    lbl_dark_background: 'Fond sombre', lbl_logo: 'Logo',
    lbl_show: 'Afficher', lbl_gp_override: 'Forcer GP',
    lbl_outlines: 'Contours', lbl_figure: 'Figure',
    lbl_starfield: 'Champ', lbl_starfield_mode: 'Mode',
    lbl_permanent_night: 'Nuit perm.', lbl_show_satellites: 'Afficher satellites',
    lbl_specified_tracker_mode: 'Suiveur spécifié',
    lbl_true_positions: 'Pos. vraies',
    lbl_gp_path_24h: 'Trajet GP (24 h)',
    lbl_ephemeris_comparison: 'Comparer éphémérides',
    lbl_precession: 'Précession', lbl_nutation: 'Nutation',
    lbl_aberration: 'Aberration', lbl_trepidation: 'Trépidation',
    lbl_source: 'Source',
    lbl_observer_lat: 'Lat obs.', lbl_observer_long: 'Long obs.',
    lbl_size: 'Taille', lbl_height: 'Hauteur',
    lbl_hq_map_art: 'Carte HQ', lbl_generated: 'Générée',
    lbl_camera_dir: 'Direction', lbl_camera_height: 'Hauteur cam.',
    lbl_camera_dist: 'Distance', lbl_zoom: 'Zoom',
    lbl_elevation: 'Élévation',
    lbl_vault_size: 'Taille voûte', lbl_vault_height: 'Hauteur voûte',
    lbl_day_of_year: 'Jour de l’année', lbl_time: 'Heure',
    lbl_datetime: 'Date-heure', lbl_timezone: 'Fuseau',
    lbl_date_time_field: 'Date / heure',
    lbl_speed: 'Vitesse',
    btn_pause: 'Pause', btn_play: 'Lecture',
    status_running: 'en cours', status_paused: 'en pause',
    btn_day: 'Jour', btn_week: 'Sem.', btn_month: 'Mois', btn_year: 'An',
    app_title: 'MODÈLE CONCEPTUEL FE',
    app_subtitle: 'Port modulaire. Toutes les distances sont sans dimension (rayon FE = 1).',
    panel_live_moon_phases: 'Phases lunaires en direct',
    panel_live_ephemeris_data: 'Données d’éphémérides en direct',
    beyond_vault: 'au-delà de la voûte optique de l’observateur',
    within_vault: 'dans la voûte optique de l’observateur',
    twilight_civil: 'crépuscule civil', twilight_nautical: 'crépuscule nautique',
    twilight_astronomical: 'crépuscule astronomique',
    daylight: 'lumière du jour', night: 'nuit',
    sun_never_leaves: '(le Soleil ne quitte pas la voûte optique)',
    sun_never_enters: '(le Soleil n’entre pas dans la voûte optique)',
    next_solar_eclipse: 'Prochaine éclipse solaire',
    next_lunar_eclipse: 'Prochaine éclipse lunaire',
    about_btn: 'À propos', legend_btn: 'Légende',
    about_p1: "Ceci est un modèle conceptuel. Les positions des étoiles sont basées uniquement sur la cinématique ; la voûte optique de l'observateur définit la limite de vision sur laquelle le champ d'étoiles est projeté.",
    about_p2: "Tout modèle conceptuel utilisant le quadrillage lat/long n'est ni meilleur ni pire qu'une autre projection. Le quadrillage est intrinsèquement cohérent et auto-référentiel à un observateur fictif au centre de la Terre — c'est cet observateur fictif qui lie le rapport 60 NMi / 1° aux positions au sol des étoiles.",
    about_p3: "« Observateur fictif » n'est pas exclusif au modèle conceptuel : chaque tel modèle est ancré dans ce qu'un observateur réel voit dans son propre hémisphère de vision, et le quadrillage n'est qu'une convention de comptabilité par-dessus.",
  },

  de: {
    tab_view: 'Ansicht', tab_time: 'Zeit', tab_show: 'Anzeigen',
    tab_tracker: 'Tracker', tab_demos: 'Demos', tab_info: 'Info',
    btn_show: 'Anzeigen', btn_gp_override: 'GP überschreiben',
    btn_enable_all: 'Alle aktivieren', btn_disable_all: 'Alle deaktivieren',
    btn_disable_satellites: 'Satelliten deaktivieren',
    tip_grids: 'FE-Gitter + optisches Gewölbe + Azimutgrade umschalten',
    tip_map: 'Kartenprojektion öffnen',
    tip_starfield: 'Sternfeld wechseln',
    tip_vault_swap: 'Himmelsgewölbe ⇄ Optisches Gewölbe tauschen',
    tip_rewind: 'Zurück', tip_play_pause: 'Wiedergabe / Pause',
    tip_fast_forward: 'Vorlauf',
    tip_slow: 'Geschwindigkeit halbieren. Setzt dabei fort.',
    tip_speed: 'Geschwindigkeit verdoppeln. Setzt dabei fort.',
    tip_end_demo: 'Aktuelle Demo stoppen',
    tip_clear_follow: 'Verfolgungsziel löschen (auch Esc)',
    tip_permanent_night: 'Permanente Nacht (dunkler Sternenhimmel)',
    tip_true_positions: 'Wahre-Position-Marker auf dem Himmelsgewölbe',
    tip_stm: 'Spezifizierter Tracker-Modus — nur Ziel fokussieren',
    tip_tracker_opts_jump: 'Zu Tracker-Optionen springen',
    tip_observer_jump: 'Zum Beobachter springen',
    tip_freecam: 'Freie Kamera — Pfeiltasten drehen die Kamera',
    tip_az_ring: 'Vollen Kompass-Ablesemodus umschalten',
    lang_label: 'Sprache',
    grp_observer: 'Beobachter', grp_camera: 'Kamera',
    grp_vault_of_heavens: 'Himmelsgewölbe', grp_optical_vault: 'Optisches Gewölbe',
    grp_body_vaults: 'Körpergewölbe', grp_rays: 'Strahlen',
    grp_cosmology: 'Kosmologie', grp_map_projection: 'Kartenprojektion',
    grp_misc: 'Sonstiges', grp_ephemeris: 'Ephemeriden',
    grp_starfield: 'Sternfeld', grp_tracker_options: 'Tracker-Optionen',
    grp_celestial_bodies: 'Himmelskörper', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Sternbilder', grp_black_holes: 'Schwarze Löcher',
    grp_quasars: 'Quasare', grp_galaxies: 'Galaxien',
    grp_satellites: 'Satelliten', grp_bright_star_catalog: 'Helle-Sterne-Katalog',
    grp_calendar: 'Kalender', grp_autoplay: 'Auto-Wiedergabe',
    grp_heavenly_vault: 'Himmelsgewölbe', grp_ground_disc: 'Boden / Scheibe',
    grp_date_time: 'Datum / Zeit',
    lbl_heavenly_vault: 'Himmelsgewölbe', lbl_vault_grid: 'Gitter',
    lbl_sun_track: 'Sonnenbahn', lbl_moon_track: 'Mondbahn',
    lbl_optical_vault: 'Optisches Gewölbe', lbl_optical_vault_grid: 'Optisches Gitter',
    lbl_azimuth_ring: 'Azimutring', lbl_facing_vector: 'Richtungsvektor / N-S-O-W',
    lbl_celestial_poles: 'Himmelspole', lbl_declination_circles: 'Deklinationskreise',
    lbl_fe_grid: 'FE-Gitter', lbl_tropics_polar: 'Tropen / Polar',
    lbl_sun_moon_gp: 'GP Sonne / Mond', lbl_longitude_ring: 'Längenring',
    lbl_shadow: 'Schatten',
    lbl_vault_rays: 'Gewölbestrahlen', lbl_optical_vault_rays: 'Optische Strahlen',
    lbl_projection_rays: 'Projektionsstrahlen', lbl_many_rays: 'Viele Strahlen',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Planeten',
    lbl_dark_background: 'Dunkler Hintergrund', lbl_logo: 'Logo',
    lbl_show: 'Anzeigen', lbl_gp_override: 'GP überschreiben',
    lbl_outlines: 'Umrisse', lbl_figure: 'Figur',
    lbl_starfield: 'Sternfeld', lbl_starfield_mode: 'Modus',
    lbl_permanent_night: 'Perm. Nacht', lbl_show_satellites: 'Satelliten anz.',
    lbl_specified_tracker_mode: 'Spez. Tracker',
    lbl_true_positions: 'Wahre Pos.',
    lbl_gp_path_24h: 'GP-Bahn (24 h)',
    lbl_ephemeris_comparison: 'Ephem. vergleichen',
    lbl_precession: 'Präzession', lbl_nutation: 'Nutation',
    lbl_aberration: 'Aberration', lbl_trepidation: 'Trepidation',
    lbl_source: 'Quelle',
    lbl_observer_lat: 'Beob. Breite', lbl_observer_long: 'Beob. Länge',
    lbl_size: 'Größe', lbl_height: 'Höhe',
    lbl_hq_map_art: 'HQ-Karte', lbl_generated: 'Generiert',
    lbl_camera_dir: 'Richtung', lbl_camera_height: 'Höhe',
    lbl_camera_dist: 'Distanz', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevation',
    lbl_vault_size: 'Gewölbegröße', lbl_vault_height: 'Gewölbehöhe',
    lbl_day_of_year: 'Tag im Jahr', lbl_time: 'Zeit',
    lbl_datetime: 'Datum-Zeit', lbl_timezone: 'Zeitzone',
    lbl_date_time_field: 'Datum / Zeit',
    lbl_speed: 'Geschwindigkeit',
    btn_pause: 'Pause', btn_play: 'Wiedergabe',
    status_running: 'läuft', status_paused: 'pausiert',
    btn_day: 'Tag', btn_week: 'Woche', btn_month: 'Monat', btn_year: 'Jahr',
    app_title: 'FE-KONZEPTMODELL',
    app_subtitle: 'Modularer Port. Alle Distanzen sind dimensionslos (FE-Radius = 1).',
    panel_live_moon_phases: 'Live-Mondphasen',
    panel_live_ephemeris_data: 'Live-Ephemeriden',
    beyond_vault: 'außerhalb des optischen Gewölbes des Beobachters',
    within_vault: 'innerhalb des optischen Gewölbes des Beobachters',
    twilight_civil: 'bürgerliche Dämmerung', twilight_nautical: 'nautische Dämmerung',
    twilight_astronomical: 'astronomische Dämmerung',
    daylight: 'Tageslicht', night: 'Nacht',
    sun_never_leaves: '(Sonne verlässt das optische Gewölbe nie)',
    sun_never_enters: '(Sonne tritt nie ins optische Gewölbe ein)',
    next_solar_eclipse: 'Nächste Sonnenfinsternis',
    next_lunar_eclipse: 'Nächste Mondfinsternis',
    about_btn: 'Über', legend_btn: 'Legende',
    about_p1: 'Dies ist ein konzeptionelles Modell. Die Positionen der Sterne basieren rein auf Kinematik; das optische Gewölbe des Beobachters definiert die Sichtgrenze, auf die das Sternfeld projiziert wird.',
    about_p2: 'Jedes konzeptionelle Modell, das das Lat/Lon-Gitter verwendet, ist nicht besser oder schlechter als jede andere Projektion. Das Gitter ist intern konsistent und selbstreferenziell zu einem fiktiven Beobachter im Erdmittelpunkt — dieser fiktive Beobachter verknüpft die Beziehung 60 NMi / 1° mit den Bodenpositionen der Sterne.',
    about_p3: '„Fiktiver Beobachter" schließt das konzeptionelle Modell nicht aus: jedes solche Modell gründet letztlich darauf, was ein einzelner realer Beobachter in seiner eigenen Sichthemisphäre sieht, und das Gitter ist nur eine Buchhaltungskonvention darüber.',
  },

  it: {
    tab_view: 'Vista', tab_time: 'Tempo', tab_show: 'Mostra',
    tab_tracker: 'Tracker', tab_demos: 'Demo', tab_info: 'Info',
    btn_show: 'Mostra', btn_gp_override: 'Forza GP',
    btn_enable_all: 'Attiva tutto', btn_disable_all: 'Disattiva tutto',
    btn_disable_satellites: 'Disattiva satelliti',
    tip_grids: 'Attiva griglia FE + ottica + gradi azimutali',
    tip_map: 'Apri impostazioni proiezione',
    tip_starfield: 'Cambia campo stelle',
    tip_vault_swap: 'Scambia Volta Celeste ⇄ Ottica',
    tip_rewind: 'Indietro', tip_play_pause: 'Play / Pausa',
    tip_fast_forward: 'Avanti veloce',
    tip_slow: 'Rallenta (metà). Riprende a questa velocità.',
    tip_speed: 'Accelera (doppio). Riprende a questa velocità.',
    tip_end_demo: 'Ferma la demo corrente',
    tip_clear_follow: 'Annulla obiettivo (anche Esc)',
    tip_permanent_night: 'Notte permanente',
    tip_true_positions: 'Marcatori posizioni reali',
    tip_stm: 'Modalità Tracker specificato',
    tip_tracker_opts_jump: 'Vai a opzioni Tracker',
    tip_observer_jump: 'Vai a Osservatore',
    tip_freecam: 'Camera libera — frecce ruotano la camera',
    tip_az_ring: 'Attiva lettura completa della bussola',
    lang_label: 'Lingua',
    grp_observer: 'Osservatore', grp_camera: 'Camera',
    grp_vault_of_heavens: 'Volta Celeste', grp_optical_vault: 'Volta Ottica',
    grp_body_vaults: 'Volte dei Corpi', grp_rays: 'Raggi',
    grp_cosmology: 'Cosmologia', grp_map_projection: 'Proiezione',
    grp_misc: 'Varie', grp_ephemeris: 'Effemeridi',
    grp_starfield: 'Campo stelle', grp_tracker_options: 'Opzioni Tracker',
    grp_celestial_bodies: 'Corpi celesti', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Costellazioni', grp_black_holes: 'Buchi neri',
    grp_quasars: 'Quasar', grp_galaxies: 'Galassie',
    grp_satellites: 'Satelliti', grp_bright_star_catalog: 'Catalogo stelle brillanti',
    grp_calendar: 'Calendario', grp_autoplay: 'Riproduzione auto',
    grp_heavenly_vault: 'Volta Celeste', grp_ground_disc: 'Suolo / Disco',
    grp_date_time: 'Data / Ora',
    lbl_heavenly_vault: 'Volta Celeste', lbl_vault_grid: 'Griglia',
    lbl_sun_track: 'Traccia Sole', lbl_moon_track: 'Traccia Luna',
    lbl_optical_vault: 'Volta Ottica', lbl_optical_vault_grid: 'Griglia Ottica',
    lbl_azimuth_ring: 'Anello azimut', lbl_facing_vector: 'Vettore / N-S-E-O',
    lbl_celestial_poles: 'Poli celesti', lbl_declination_circles: 'Cerchi decl.',
    lbl_fe_grid: 'Griglia FE', lbl_tropics_polar: 'Tropici / polari',
    lbl_sun_moon_gp: 'GP Sole / Luna', lbl_longitude_ring: 'Anello longitudine',
    lbl_shadow: 'Ombra',
    lbl_vault_rays: 'Raggi Volta', lbl_optical_vault_rays: 'Raggi Ottici',
    lbl_projection_rays: 'Raggi Proiezione', lbl_many_rays: 'Molti raggi',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Pianeti',
    lbl_dark_background: 'Sfondo scuro', lbl_logo: 'Logo',
    lbl_show: 'Mostra', lbl_gp_override: 'Forza GP',
    lbl_outlines: 'Contorni', lbl_figure: 'Figura',
    lbl_starfield: 'Campo', lbl_starfield_mode: 'Modalità',
    lbl_permanent_night: 'Notte perm.', lbl_show_satellites: 'Mostra satelliti',
    lbl_specified_tracker_mode: 'Tracker spec.',
    lbl_true_positions: 'Pos. reali',
    lbl_gp_path_24h: 'Tragitto GP (24 h)',
    lbl_ephemeris_comparison: 'Confronto effemeridi',
    lbl_precession: 'Precessione', lbl_nutation: 'Nutazione',
    lbl_aberration: 'Aberrazione', lbl_trepidation: 'Trepidazione',
    lbl_source: 'Sorgente',
    lbl_observer_lat: 'Lat oss.', lbl_observer_long: 'Long oss.',
    lbl_size: 'Dimensione', lbl_height: 'Altezza',
    lbl_hq_map_art: 'Mappa HQ', lbl_generated: 'Generata',
    lbl_camera_dir: 'Direzione', lbl_camera_height: 'Altezza',
    lbl_camera_dist: 'Distanza', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevazione',
    lbl_vault_size: 'Dimensione volta', lbl_vault_height: 'Altezza volta',
    lbl_day_of_year: 'Giorno dell’anno', lbl_time: 'Ora',
    lbl_datetime: 'Data-ora', lbl_timezone: 'Fuso orario',
    lbl_date_time_field: 'Data / ora',
    lbl_speed: 'Velocità',
    btn_pause: 'Pausa', btn_play: 'Play',
    status_running: 'in esecuzione', status_paused: 'in pausa',
    btn_day: 'Giorno', btn_week: 'Sett.', btn_month: 'Mese', btn_year: 'Anno',
    app_title: 'MODELLO CONCETTUALE FE',
    app_subtitle: 'Port modulare. Tutte le distanze sono adimensionali (raggio FE = 1).',
    panel_live_moon_phases: 'Fasi lunari in diretta',
    panel_live_ephemeris_data: 'Effemeridi in diretta',
    beyond_vault: 'oltre la volta ottica dell’osservatore',
    within_vault: 'dentro la volta ottica dell’osservatore',
    twilight_civil: 'crepuscolo civile', twilight_nautical: 'crepuscolo nautico',
    twilight_astronomical: 'crepuscolo astronomico',
    daylight: 'luce diurna', night: 'notte',
    sun_never_leaves: '(il Sole non lascia la volta ottica)',
    sun_never_enters: '(il Sole non entra nella volta ottica)',
    next_solar_eclipse: 'Prossima eclissi solare',
    next_lunar_eclipse: 'Prossima eclissi lunare',
    about_btn: 'Info', legend_btn: 'Legenda',
    about_p1: "Questo è un modello concettuale. Le posizioni delle stelle si basano puramente sulla cinematica; la volta ottica dell'osservatore definisce il limite della visione su cui viene proiettato il campo stellare.",
    about_p2: "Qualsiasi modello concettuale che utilizzi il reticolo lat/long non è né migliore né peggiore di qualsiasi altra proiezione. Il reticolo è internamente coerente e auto-referenziale a un osservatore fittizio al centro della Terra — è quell'osservatore fittizio che lega il rapporto 60 NMi / 1° alle posizioni terrestri delle stelle.",
    about_p3: "\"Osservatore fittizio\" non è mutuamente esclusivo con il modello concettuale: ogni tale modello si fonda in definitiva su ciò che un singolo osservatore reale vede nel proprio emisfero visivo, e il reticolo è semplicemente una convenzione contabile su questo.",
  },

  pt: {
    tab_view: 'Vista', tab_time: 'Tempo', tab_show: 'Mostrar',
    tab_tracker: 'Rastreador', tab_demos: 'Demos', tab_info: 'Info',
    btn_show: 'Mostrar', btn_gp_override: 'Forçar GP',
    btn_enable_all: 'Ativar tudo', btn_disable_all: 'Desativar tudo',
    btn_disable_satellites: 'Desativar satélites',
    tip_grids: 'Alternar grade FE + óptica + graus azimutais',
    tip_map: 'Abrir projeção do mapa',
    tip_starfield: 'Trocar campo de estrelas',
    tip_vault_swap: 'Trocar Abóbada Celeste ⇄ Óptica',
    tip_rewind: 'Retroceder', tip_play_pause: 'Reproduzir / Pausar',
    tip_fast_forward: 'Avançar rápido',
    tip_slow: 'Reduzir (metade). Retoma nesta velocidade.',
    tip_speed: 'Acelerar (dobro). Retoma nesta velocidade.',
    tip_end_demo: 'Parar demo atual',
    tip_clear_follow: 'Limpar alvo (Esc também)',
    tip_permanent_night: 'Noite permanente',
    tip_true_positions: 'Marcadores de posições verdadeiras',
    tip_stm: 'Modo Rastreador específico',
    tip_tracker_opts_jump: 'Ir a Opções do Rastreador',
    tip_observer_jump: 'Ir ao Observador',
    tip_freecam: 'Câmera livre — setas giram a câmera',
    tip_az_ring: 'Alternar leitura completa da bússola',
    lang_label: 'Idioma',
    grp_observer: 'Observador', grp_camera: 'Câmera',
    grp_vault_of_heavens: 'Abóbada Celeste', grp_optical_vault: 'Abóbada Óptica',
    grp_body_vaults: 'Abóbadas de Corpos', grp_rays: 'Raios',
    grp_cosmology: 'Cosmologia', grp_map_projection: 'Projeção',
    grp_misc: 'Diversos', grp_ephemeris: 'Efemérides',
    grp_starfield: 'Campo estrelas', grp_tracker_options: 'Opções',
    grp_celestial_bodies: 'Corpos celestes', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Constelações', grp_black_holes: 'Buracos Negros',
    grp_quasars: 'Quasares', grp_galaxies: 'Galáxias',
    grp_satellites: 'Satélites', grp_bright_star_catalog: 'Catálogo de Estrelas Brilhantes',
    grp_calendar: 'Calendário', grp_autoplay: 'Auto-reprodução',
    grp_heavenly_vault: 'Abóbada Celeste', grp_ground_disc: 'Solo / Disco',
    grp_date_time: 'Data / Hora',
    lbl_heavenly_vault: 'Abóbada Celeste', lbl_vault_grid: 'Grade',
    lbl_sun_track: 'Trajetória Sol', lbl_moon_track: 'Trajetória Lua',
    lbl_optical_vault: 'Abóbada Óptica', lbl_optical_vault_grid: 'Grade Óptica',
    lbl_azimuth_ring: 'Anel azimute', lbl_facing_vector: 'Vetor / N-S-L-O',
    lbl_celestial_poles: 'Polos celestes', lbl_declination_circles: 'Círculos decl.',
    lbl_fe_grid: 'Grade FE', lbl_tropics_polar: 'Trópicos / polar',
    lbl_sun_moon_gp: 'GP Sol / Lua', lbl_longitude_ring: 'Anel longitude',
    lbl_shadow: 'Sombra',
    lbl_vault_rays: 'Raios Abóbada', lbl_optical_vault_rays: 'Raios Ópticos',
    lbl_projection_rays: 'Raios Projeção', lbl_many_rays: 'Muitos raios',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Planetas',
    lbl_dark_background: 'Fundo escuro', lbl_logo: 'Logo',
    lbl_show: 'Mostrar', lbl_gp_override: 'Forçar GP',
    lbl_outlines: 'Contornos', lbl_figure: 'Figura',
    lbl_starfield: 'Campo', lbl_starfield_mode: 'Modo',
    lbl_permanent_night: 'Noite perm.', lbl_show_satellites: 'Mostrar satélites',
    lbl_specified_tracker_mode: 'Rastreador esp.',
    lbl_true_positions: 'Pos. verdadeiras',
    lbl_gp_path_24h: 'Trajeto GP (24 h)',
    lbl_ephemeris_comparison: 'Comparar efemérides',
    lbl_precession: 'Precessão', lbl_nutation: 'Nutação',
    lbl_aberration: 'Aberração', lbl_trepidation: 'Trepidação',
    lbl_source: 'Fonte',
    lbl_observer_lat: 'Lat obs.', lbl_observer_long: 'Long obs.',
    lbl_size: 'Tamanho', lbl_height: 'Altura',
    lbl_hq_map_art: 'Mapa HQ', lbl_generated: 'Gerado',
    lbl_camera_dir: 'Direção', lbl_camera_height: 'Altura',
    lbl_camera_dist: 'Distância', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevação',
    lbl_vault_size: 'Tamanho abóbada', lbl_vault_height: 'Altura abóbada',
    lbl_day_of_year: 'Dia do ano', lbl_time: 'Hora',
    lbl_datetime: 'Data-hora', lbl_timezone: 'Fuso',
    lbl_date_time_field: 'Data / hora',
    lbl_speed: 'Velocidade',
    btn_pause: 'Pausa', btn_play: 'Reproduzir',
    status_running: 'em execução', status_paused: 'pausado',
    btn_day: 'Dia', btn_week: 'Sem.', btn_month: 'Mês', btn_year: 'Ano',
    app_title: 'MODELO CONCEITUAL FE',
    app_subtitle: 'Port modular. Todas as distâncias são adimensionais (raio FE = 1).',
    panel_live_moon_phases: 'Fases Lunares ao Vivo',
    panel_live_ephemeris_data: 'Efemérides ao Vivo',
    beyond_vault: 'além da abóbada óptica do observador',
    within_vault: 'dentro da abóbada óptica do observador',
    twilight_civil: 'crepúsculo civil', twilight_nautical: 'crepúsculo náutico',
    twilight_astronomical: 'crepúsculo astronômico',
    daylight: 'luz do dia', night: 'noite',
    sun_never_leaves: '(o Sol nunca deixa a abóbada óptica)',
    sun_never_enters: '(o Sol nunca entra na abóbada óptica)',
    next_solar_eclipse: 'Próximo eclipse solar',
    next_lunar_eclipse: 'Próximo eclipse lunar',
    about_btn: 'Sobre', legend_btn: 'Legenda',
    about_p1: 'Este é um modelo conceitual. As posições das estrelas são baseadas puramente em cinemática; a abóbada óptica do observador define o limite de visão sobre o qual o campo de estrelas é projetado.',
    about_p2: 'Qualquer modelo conceitual que use a grade lat/long não é nem melhor nem pior que qualquer outra projeção. A grade é internamente consistente e auto-referencial a um observador fictício no centro da Terra — é esse observador fictício que liga a relação 60 NMi / 1° às posições terrestres das estrelas.',
    about_p3: '"Observador fictício" não é mutuamente exclusivo do modelo conceitual: cada modelo desse tipo está, em última análise, ancorado no que um único observador real vê em seu próprio hemisfério de visão, e a grade é simplesmente uma convenção contábil sobre isso.',
  },

  pl: {
    tab_view: 'Widok', tab_time: 'Czas', tab_show: 'Pokaż',
    tab_tracker: 'Tracker', tab_demos: 'Dema', tab_info: 'Info',
    btn_show: 'Pokaż', btn_gp_override: 'Wymuś GP',
    btn_enable_all: 'Włącz wszystko', btn_disable_all: 'Wyłącz wszystko',
    btn_disable_satellites: 'Wyłącz satelity',
    tip_grids: 'Przełącz siatkę FE + optyczną + stopnie azymutu',
    tip_map: 'Otwórz ustawienia projekcji',
    tip_starfield: 'Zmień pole gwiazd',
    tip_vault_swap: 'Zamień Sklepienie Niebieskie ⇄ Optyczne',
    tip_rewind: 'Przewiń', tip_play_pause: 'Odtwarzaj / Pauza',
    tip_fast_forward: 'Szybko naprzód',
    tip_slow: 'Zwolnij (połowa). Wznawia z tą prędkością.',
    tip_speed: 'Przyspiesz (podwójnie). Wznawia z tą prędkością.',
    tip_end_demo: 'Zatrzymaj bieżące demo',
    tip_clear_follow: 'Wyczyść cel (działa też Esc)',
    tip_permanent_night: 'Stała noc',
    tip_true_positions: 'Markery prawdziwych pozycji',
    tip_stm: 'Tryb określonego trackera',
    tip_tracker_opts_jump: 'Przejdź do opcji trackera',
    tip_observer_jump: 'Przejdź do obserwatora',
    tip_freecam: 'Wolna kamera — strzałki obracają kamerą',
    tip_az_ring: 'Przełącz pełny odczyt kompasu',
    lang_label: 'Język',
    grp_observer: 'Obserwator', grp_camera: 'Kamera',
    grp_vault_of_heavens: 'Sklepienie Niebieskie', grp_optical_vault: 'Sklepienie Optyczne',
    grp_body_vaults: 'Sklepienia ciał', grp_rays: 'Promienie',
    grp_cosmology: 'Kosmologia', grp_map_projection: 'Projekcja mapy',
    grp_misc: 'Różne', grp_ephemeris: 'Efemerydy',
    grp_starfield: 'Pole gwiazd', grp_tracker_options: 'Opcje trackera',
    grp_celestial_bodies: 'Ciała niebieskie', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Gwiazdozbiory', grp_black_holes: 'Czarne dziury',
    grp_quasars: 'Kwazary', grp_galaxies: 'Galaktyki',
    grp_satellites: 'Satelity', grp_bright_star_catalog: 'Katalog jasnych gwiazd',
    grp_calendar: 'Kalendarz', grp_autoplay: 'Auto-odtwarzanie',
    grp_heavenly_vault: 'Sklepienie Niebieskie', grp_ground_disc: 'Ziemia / Dysk',
    grp_date_time: 'Data / Czas',
    lbl_heavenly_vault: 'Sklepienie', lbl_vault_grid: 'Siatka',
    lbl_sun_track: 'Tor Słońca', lbl_moon_track: 'Tor Księżyca',
    lbl_optical_vault: 'Sklep. opt.', lbl_optical_vault_grid: 'Siatka opt.',
    lbl_azimuth_ring: 'Pierścień azymutu', lbl_facing_vector: 'Wektor / N-S-E-W',
    lbl_celestial_poles: 'Bieguny niebieskie', lbl_declination_circles: 'Koła deklinacji',
    lbl_fe_grid: 'Siatka FE', lbl_tropics_polar: 'Zwrotniki / polarne',
    lbl_sun_moon_gp: 'GP Słońca / Księż.', lbl_longitude_ring: 'Pierścień dług.',
    lbl_shadow: 'Cień',
    lbl_vault_rays: 'Promienie skl.', lbl_optical_vault_rays: 'Promienie opt.',
    lbl_projection_rays: 'Promienie proj.', lbl_many_rays: 'Wiele promieni',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Planety',
    lbl_dark_background: 'Ciemne tło', lbl_logo: 'Logo',
    lbl_show: 'Pokaż', lbl_gp_override: 'Wymuś GP',
    lbl_outlines: 'Kontury', lbl_figure: 'Postać',
    lbl_starfield: 'Pole', lbl_starfield_mode: 'Tryb',
    lbl_permanent_night: 'Stała noc', lbl_show_satellites: 'Pokaż satelity',
    lbl_specified_tracker_mode: 'Tracker określ.',
    lbl_true_positions: 'Praw. pozycje',
    lbl_gp_path_24h: 'Trasa GP (24 h)',
    lbl_ephemeris_comparison: 'Porównanie efemeryd',
    lbl_precession: 'Precesja', lbl_nutation: 'Nutacja',
    lbl_aberration: 'Aberracja', lbl_trepidation: 'Trepidacja',
    lbl_source: 'Źródło',
    lbl_observer_lat: 'Szer. obs.', lbl_observer_long: 'Dług. obs.',
    lbl_size: 'Rozmiar', lbl_height: 'Wysokość',
    lbl_hq_map_art: 'HQ Mapa', lbl_generated: 'Wygenerowane',
    lbl_camera_dir: 'Kierunek', lbl_camera_height: 'Wysokość',
    lbl_camera_dist: 'Odległość', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elewacja',
    lbl_vault_size: 'Rozmiar skl.', lbl_vault_height: 'Wysokość skl.',
    lbl_day_of_year: 'Dzień roku', lbl_time: 'Czas',
    lbl_datetime: 'Data-czas', lbl_timezone: 'Strefa czas.',
    lbl_date_time_field: 'Data / czas',
    lbl_speed: 'Prędkość',
    btn_pause: 'Pauza', btn_play: 'Odtwarzaj',
    status_running: 'działa', status_paused: 'wstrzymane',
    btn_day: 'Dzień', btn_week: 'Tydz.', btn_month: 'Mies.', btn_year: 'Rok',
    app_title: 'KONCEPCYJNY MODEL FE',
    app_subtitle: 'Modułowy port. Wszystkie odległości są bezwymiarowe (promień FE = 1).',
    panel_live_moon_phases: 'Fazy Księżyca na żywo',
    panel_live_ephemeris_data: 'Efemerydy na żywo',
    beyond_vault: 'poza optycznym sklepieniem obserwatora',
    within_vault: 'w optycznym sklepieniu obserwatora',
    twilight_civil: 'zmierzch cywilny', twilight_nautical: 'zmierzch żeglarski',
    twilight_astronomical: 'zmierzch astronomiczny',
    daylight: 'światło dzienne', night: 'noc',
    sun_never_leaves: '(Słońce nie opuszcza optycznego sklepienia)',
    sun_never_enters: '(Słońce nie wchodzi w optyczne sklepienie)',
    next_solar_eclipse: 'Następne zaćmienie Słońca',
    next_lunar_eclipse: 'Następne zaćmienie Księżyca',
    about_btn: 'O modelu', legend_btn: 'Legenda',
    about_p1: 'To jest model koncepcyjny. Pozycje gwiazd opierają się wyłącznie na kinematyce; optyczne sklepienie obserwatora definiuje granicę widzenia, na którą rzutowane jest pole gwiazd.',
    about_p2: 'Każdy model koncepcyjny używający siatki szer./dług. nie jest ani lepszy, ani gorszy niż jakakolwiek inna projekcja. Siatka jest wewnętrznie spójna i samoodniesieniowa wobec fikcyjnego obserwatora w centrum Ziemi — to ten fikcyjny obserwator wiąże relację 60 NMi / 1° z naziemnymi pozycjami gwiazd.',
    about_p3: '„Fikcyjny obserwator" nie wyklucza modelu koncepcyjnego: każdy taki model jest ostatecznie zakorzeniony w tym, co pojedynczy rzeczywisty obserwator widzi we własnej hemisferze widzenia, a siatka jest po prostu konwencją księgową na tym osadzoną.',
  },

  nl: {
    tab_view: 'Weergave', tab_time: 'Tijd', tab_show: 'Tonen',
    tab_tracker: 'Tracker', tab_demos: 'Demos', tab_info: 'Info',
    btn_show: 'Tonen', btn_gp_override: 'GP overschrijven',
    btn_enable_all: 'Alles inschakelen', btn_disable_all: 'Alles uit',
    btn_disable_satellites: 'Satellieten uit',
    tip_grids: 'FE-raster + optisch raster + azimutgraden omschakelen',
    tip_map: 'Kaartprojectie openen',
    tip_starfield: 'Sterrenveld wisselen',
    tip_vault_swap: 'Hemels ⇄ Optisch gewelf wisselen',
    tip_rewind: 'Terug', tip_play_pause: 'Afspelen / Pauze',
    tip_fast_forward: 'Snel vooruit',
    tip_slow: 'Vertragen (helft). Hervat op deze snelheid.',
    tip_speed: 'Versnellen (dubbel). Hervat op deze snelheid.',
    tip_end_demo: 'Huidige demo stoppen',
    tip_clear_follow: 'Volgdoel wissen (Esc werkt ook)',
    tip_permanent_night: 'Permanente nacht',
    tip_true_positions: 'Echte-positie markers',
    tip_stm: 'Specifieke tracker-modus',
    tip_tracker_opts_jump: 'Naar Tracker-opties',
    tip_observer_jump: 'Naar Waarnemer',
    tip_freecam: 'Vrije camera — pijltjes draaien de camera',
    tip_az_ring: 'Volledige kompasweergave',
    lang_label: 'Taal',
    grp_observer: 'Waarnemer', grp_camera: 'Camera',
    grp_vault_of_heavens: 'Hemelgewelf', grp_optical_vault: 'Optisch Gewelf',
    grp_body_vaults: 'Lichaamsgewelven', grp_rays: 'Stralen',
    grp_cosmology: 'Kosmologie', grp_map_projection: 'Kaartprojectie',
    grp_misc: 'Diversen', grp_ephemeris: 'Efemeriden',
    grp_starfield: 'Sterrenveld', grp_tracker_options: 'Tracker-opties',
    grp_celestial_bodies: 'Hemellichamen', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Sterrenbeelden', grp_black_holes: 'Zwarte gaten',
    grp_quasars: 'Quasars', grp_galaxies: 'Sterrenstelsels',
    grp_satellites: 'Satellieten', grp_bright_star_catalog: 'Heldere-sterren-catalogus',
    grp_calendar: 'Kalender', grp_autoplay: 'Auto-afspelen',
    grp_heavenly_vault: 'Hemelgewelf', grp_ground_disc: 'Grond / Schijf',
    grp_date_time: 'Datum / Tijd',
    lbl_heavenly_vault: 'Hemelgewelf', lbl_vault_grid: 'Raster',
    lbl_sun_track: 'Zonbaan', lbl_moon_track: 'Maanbaan',
    lbl_optical_vault: 'Opt. gewelf', lbl_optical_vault_grid: 'Opt. raster',
    lbl_azimuth_ring: 'Azimutring', lbl_facing_vector: 'Vector / N-Z-O-W',
    lbl_celestial_poles: 'Hemelpolen', lbl_declination_circles: 'Decl.cirkels',
    lbl_fe_grid: 'FE-raster', lbl_tropics_polar: 'Tropen / polair',
    lbl_sun_moon_gp: 'GP zon / maan', lbl_longitude_ring: 'Lengtering',
    lbl_shadow: 'Schaduw',
    lbl_vault_rays: 'Gewelfstralen', lbl_optical_vault_rays: 'Opt. stralen',
    lbl_projection_rays: 'Projectiestralen', lbl_many_rays: 'Veel stralen',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Planeten',
    lbl_dark_background: 'Donkere achtergrond', lbl_logo: 'Logo',
    lbl_show: 'Tonen', lbl_gp_override: 'GP overschr.',
    lbl_outlines: 'Contouren', lbl_figure: 'Figuur',
    lbl_starfield: 'Veld', lbl_starfield_mode: 'Modus',
    lbl_permanent_night: 'Perm. nacht', lbl_show_satellites: 'Toon sat.',
    lbl_specified_tracker_mode: 'Spec. tracker',
    lbl_true_positions: 'Echte pos.',
    lbl_gp_path_24h: 'GP-pad (24 u)',
    lbl_ephemeris_comparison: 'Efem. vergelijken',
    lbl_precession: 'Precessie', lbl_nutation: 'Nutatie',
    lbl_aberration: 'Aberratie', lbl_trepidation: 'Trepidatie',
    lbl_source: 'Bron',
    lbl_observer_lat: 'Br. waarn.', lbl_observer_long: 'Lng waarn.',
    lbl_size: 'Grootte', lbl_height: 'Hoogte',
    lbl_hq_map_art: 'HQ Kaart', lbl_generated: 'Gegenereerd',
    lbl_camera_dir: 'Richting', lbl_camera_height: 'Hoogte',
    lbl_camera_dist: 'Afstand', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevatie',
    lbl_vault_size: 'Gewelfgrootte', lbl_vault_height: 'Gewelfhoogte',
    lbl_day_of_year: 'Dag v/h jaar', lbl_time: 'Tijd',
    lbl_datetime: 'Datum-tijd', lbl_timezone: 'Tijdzone',
    lbl_date_time_field: 'Datum / tijd',
    lbl_speed: 'Snelheid',
    btn_pause: 'Pauze', btn_play: 'Afspelen',
    status_running: 'actief', status_paused: 'gepauzeerd',
    btn_day: 'Dag', btn_week: 'Week', btn_month: 'Maand', btn_year: 'Jaar',
    app_title: 'FE-CONCEPTUEEL MODEL',
    app_subtitle: 'Modulaire port. Alle afstanden zijn dimensieloos (FE-straal = 1).',
    panel_live_moon_phases: 'Live maanfasen',
    panel_live_ephemeris_data: 'Live efemeriden',
    beyond_vault: 'voorbij het optische gewelf van de waarnemer',
    within_vault: 'binnen het optische gewelf van de waarnemer',
    twilight_civil: 'burgerlijke schemering', twilight_nautical: 'nautische schemering',
    twilight_astronomical: 'astronomische schemering',
    daylight: 'daglicht', night: 'nacht',
    sun_never_leaves: '(zon verlaat het optische gewelf nooit)',
    sun_never_enters: '(zon komt niet in het optische gewelf)',
    next_solar_eclipse: 'Volgende zonsverduistering',
    next_lunar_eclipse: 'Volgende maansverduistering',
    about_btn: 'Over', legend_btn: 'Legenda',
    about_p1: 'Dit is een conceptueel model. De posities van de sterren zijn puur gebaseerd op kinematica; het optische gewelf van de waarnemer definieert de zichtgrens waarop het sterrenveld wordt geprojecteerd.',
    about_p2: 'Elk conceptueel model dat het lat/long-raster gebruikt is niet beter of slechter dan welke andere projectie. Het raster is intern consistent en zelfreferentieel naar een fictieve waarnemer in het centrum van de aarde — die fictieve waarnemer verbindt de 60 NMi / 1°-relatie met de grondposities van de sterren.',
    about_p3: '"Fictieve waarnemer" sluit het conceptuele model niet uit: elk zo\'n model is uiteindelijk geworteld in wat één werkelijke waarnemer ziet in zijn eigen zichthalfrond, en het raster is gewoon één boekhoudconventie daarbovenop.',
  },

  sk: {
    tab_view: 'Pohľad', tab_time: 'Čas', tab_show: 'Zobraziť',
    tab_tracker: 'Sledovač', tab_demos: 'Ukážky', tab_info: 'Info',
    btn_show: 'Zobraziť', btn_gp_override: 'Prepis GP',
    btn_enable_all: 'Povoliť všetko', btn_disable_all: 'Zakázať všetko',
    btn_disable_satellites: 'Zakázať satelity',
    tip_grids: 'Prepnúť FE mriežku + optickú mriežku + azimutové stupne',
    tip_map: 'Otvoriť nastavenia projekcie',
    tip_starfield: 'Prepnúť hviezdne pole',
    tip_vault_swap: 'Prepnúť Nebeskú ⇄ Optickú klenbu',
    tip_rewind: 'Späť', tip_play_pause: 'Prehrať / Pauza',
    tip_fast_forward: 'Rýchlo vpred',
    tip_slow: 'Spomaliť (polovica). Pokračuje touto rýchlosťou.',
    tip_speed: 'Zrýchliť (dvojnásobok). Pokračuje touto rýchlosťou.',
    tip_end_demo: 'Zastaviť aktuálnu ukážku',
    tip_clear_follow: 'Zrušiť cieľ (aj Esc)',
    tip_permanent_night: 'Trvalá noc',
    tip_true_positions: 'Značky skutočných pozícií',
    tip_stm: 'Režim určeného sledovača',
    tip_tracker_opts_jump: 'Prejsť na možnosti sledovača',
    tip_observer_jump: 'Prejsť na pozorovateľa',
    tip_freecam: 'Voľná kamera — šípky otáčajú kameru',
    tip_az_ring: 'Prepnúť úplný kompas',
    lang_label: 'Jazyk',
    grp_observer: 'Pozorovateľ', grp_camera: 'Kamera',
    grp_vault_of_heavens: 'Nebeská klenba', grp_optical_vault: 'Optická klenba',
    grp_body_vaults: 'Klenby telies', grp_rays: 'Lúče',
    grp_cosmology: 'Kozmológia', grp_map_projection: 'Projekcia mapy',
    grp_misc: 'Rôzne', grp_ephemeris: 'Efemeridy',
    grp_starfield: 'Hviezdne pole', grp_tracker_options: 'Možnosti',
    grp_celestial_bodies: 'Nebeské telesá', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Súhvezdia', grp_black_holes: 'Čierne diery',
    grp_quasars: 'Kvazary', grp_galaxies: 'Galaxie',
    grp_satellites: 'Satelity', grp_bright_star_catalog: 'Katalóg jasných hviezd',
    grp_calendar: 'Kalendár', grp_autoplay: 'Auto prehrávanie',
    grp_heavenly_vault: 'Nebeská klenba', grp_ground_disc: 'Zem / Disk',
    grp_date_time: 'Dátum / Čas',
    lbl_heavenly_vault: 'Klenba', lbl_vault_grid: 'Mriežka',
    lbl_sun_track: 'Stopa Slnka', lbl_moon_track: 'Stopa Mesiaca',
    lbl_optical_vault: 'Opt. klenba', lbl_optical_vault_grid: 'Opt. mriežka',
    lbl_azimuth_ring: 'Azimutový kruh', lbl_facing_vector: 'Vektor / S-J-V-Z',
    lbl_celestial_poles: 'Nebeské póly', lbl_declination_circles: 'Decl. kruhy',
    lbl_fe_grid: 'FE mriežka', lbl_tropics_polar: 'Obratníky / polárne',
    lbl_sun_moon_gp: 'GP Slnka / Mes.', lbl_longitude_ring: 'Kruh dĺžky',
    lbl_shadow: 'Tieň',
    lbl_vault_rays: 'Lúče klenby', lbl_optical_vault_rays: 'Opt. lúče',
    lbl_projection_rays: 'Lúče projekcie', lbl_many_rays: 'Veľa lúčov',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Planéty',
    lbl_dark_background: 'Tmavé pozadie', lbl_logo: 'Logo',
    lbl_show: 'Zobraziť', lbl_gp_override: 'Prepis GP',
    lbl_outlines: 'Obrysy', lbl_figure: 'Postava',
    lbl_starfield: 'Pole', lbl_starfield_mode: 'Režim',
    lbl_permanent_night: 'Trv. noc', lbl_show_satellites: 'Zobr. satelity',
    lbl_specified_tracker_mode: 'Určený sledovač',
    lbl_true_positions: 'Skut. pozície',
    lbl_gp_path_24h: 'Trasa GP (24 h)',
    lbl_ephemeris_comparison: 'Porovnanie efem.',
    lbl_precession: 'Precesia', lbl_nutation: 'Nutácia',
    lbl_aberration: 'Aberácia', lbl_trepidation: 'Trepidácia',
    lbl_source: 'Zdroj',
    lbl_observer_lat: 'Šírka poz.', lbl_observer_long: 'Dĺžka poz.',
    lbl_size: 'Veľkosť', lbl_height: 'Výška',
    lbl_hq_map_art: 'HQ Mapa', lbl_generated: 'Generované',
    lbl_camera_dir: 'Smer', lbl_camera_height: 'Výška',
    lbl_camera_dist: 'Vzdial.', lbl_zoom: 'Zoom',
    lbl_elevation: 'Elevácia',
    lbl_vault_size: 'Veľk. klenby', lbl_vault_height: 'Výška klenby',
    lbl_day_of_year: 'Deň roka', lbl_time: 'Čas',
    lbl_datetime: 'Dátum-čas', lbl_timezone: 'Časové pásmo',
    lbl_date_time_field: 'Dátum / čas',
    lbl_speed: 'Rýchlosť',
    btn_pause: 'Pauza', btn_play: 'Prehrať',
    status_running: 'beží', status_paused: 'pozastavené',
    btn_day: 'Deň', btn_week: 'Týž.', btn_month: 'Mes.', btn_year: 'Rok',
    app_title: 'KONCEPČNÝ MODEL FE',
    app_subtitle: 'Modulárny port. Všetky vzdialenosti sú bezrozmerné (FE polomer = 1).',
    panel_live_moon_phases: 'Živé fázy Mesiaca',
    panel_live_ephemeris_data: 'Živé dáta efemerid',
    beyond_vault: 'mimo optickú klenbu pozorovateľa',
    within_vault: 'vo vnútri optickej klenby pozorovateľa',
    twilight_civil: 'občiansky súmrak', twilight_nautical: 'námorný súmrak',
    twilight_astronomical: 'astronomický súmrak',
    daylight: 'denné svetlo', night: 'noc',
    sun_never_leaves: '(Slnko neopúšťa optickú klenbu)',
    sun_never_enters: '(Slnko nevstupuje do optickej klenby)',
    next_solar_eclipse: 'Ďalšie zatmenie Slnka',
    next_lunar_eclipse: 'Ďalšie zatmenie Mesiaca',
    about_btn: 'O modeli', legend_btn: 'Legenda',
    about_p1: 'Toto je koncepčný model. Pozície hviezd sú založené čisto na kinematike; optická klenba pozorovateľa definuje hranicu viditeľnosti, na ktorú sa premieta hviezdne pole.',
    about_p2: 'Žiadny koncepčný model používajúci mriežku zem. šírky/dĺžky nie je lepší ani horší než akákoľvek iná projekcia. Mriežka je vnútorne konzistentná a samoreferenčná voči fiktívnemu pozorovateľovi v strede Zeme — práve tento fiktívny pozorovateľ viaže vzťah 60 NMi / 1° na pozície hviezd na zemi.',
    about_p3: '„Fiktívny pozorovateľ" sa nevylučuje s koncepčným modelom: každý taký model je nakoniec zakotvený v tom, čo jeden skutočný pozorovateľ vidí vo svojej pologuli vízie, a mriežka je iba jedna účtovná konvencia nad tým.',
  },

  ru: {
    tab_view: 'Вид', tab_time: 'Время', tab_show: 'Показать',
    tab_tracker: 'Трекер', tab_demos: 'Демо', tab_info: 'Инфо',
    btn_show: 'Показать', btn_gp_override: 'Переопр. GP',
    btn_enable_all: 'Включить всё', btn_disable_all: 'Выключить всё',
    btn_disable_satellites: 'Выкл. спутники',
    tip_grids: 'Переключить сетку FE + оптическую + градусы азимута',
    tip_map: 'Открыть настройки проекции',
    tip_starfield: 'Сменить звёздное поле',
    tip_vault_swap: 'Переключить Небесный ⇄ Оптический свод',
    tip_rewind: 'Назад', tip_play_pause: 'Воспроизвести / Пауза',
    tip_fast_forward: 'Перемотка вперёд',
    tip_slow: 'Замедлить (вдвое). Продолжает с этой скоростью.',
    tip_speed: 'Ускорить (вдвое). Продолжает с этой скоростью.',
    tip_end_demo: 'Остановить текущее демо',
    tip_clear_follow: 'Сбросить цель (также Esc)',
    tip_permanent_night: 'Постоянная ночь',
    tip_true_positions: 'Маркеры истинных позиций',
    tip_stm: 'Режим заданного трекера',
    tip_tracker_opts_jump: 'Перейти к опциям трекера',
    tip_observer_jump: 'Перейти к наблюдателю',
    tip_freecam: 'Свободная камера — стрелки вращают камеру',
    tip_az_ring: 'Полное считывание компаса',
    lang_label: 'Язык',
    grp_observer: 'Наблюдатель', grp_camera: 'Камера',
    grp_vault_of_heavens: 'Небесный свод', grp_optical_vault: 'Оптический свод',
    grp_body_vaults: 'Своды тел', grp_rays: 'Лучи',
    grp_cosmology: 'Космология', grp_map_projection: 'Проекция карты',
    grp_misc: 'Разное', grp_ephemeris: 'Эфемериды',
    grp_starfield: 'Звёздное поле', grp_tracker_options: 'Опции трекера',
    grp_celestial_bodies: 'Небесные тела', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'Созвездия', grp_black_holes: 'Чёрные дыры',
    grp_quasars: 'Квазары', grp_galaxies: 'Галактики',
    grp_satellites: 'Спутники', grp_bright_star_catalog: 'Каталог ярких звёзд',
    grp_calendar: 'Календарь', grp_autoplay: 'Автовоспр.',
    grp_heavenly_vault: 'Небесный свод', grp_ground_disc: 'Земля / Диск',
    grp_date_time: 'Дата / Время',
    lbl_heavenly_vault: 'Небесный свод', lbl_vault_grid: 'Сетка',
    lbl_sun_track: 'Путь Солнца', lbl_moon_track: 'Путь Луны',
    lbl_optical_vault: 'Опт. свод', lbl_optical_vault_grid: 'Опт. сетка',
    lbl_azimuth_ring: 'Азимут. кольцо', lbl_facing_vector: 'Вектор / С-Ю-В-З',
    lbl_celestial_poles: 'Небесные полюса', lbl_declination_circles: 'Круги склон.',
    lbl_fe_grid: 'Сетка FE', lbl_tropics_polar: 'Тропики / полярные',
    lbl_sun_moon_gp: 'GP Солнце / Луна', lbl_longitude_ring: 'Кольцо долготы',
    lbl_shadow: 'Тень',
    lbl_vault_rays: 'Лучи свода', lbl_optical_vault_rays: 'Опт. лучи',
    lbl_projection_rays: 'Лучи проекции', lbl_many_rays: 'Много лучей',
    lbl_axis_mundi: 'Axis Mundi', lbl_planets: 'Планеты',
    lbl_dark_background: 'Тёмный фон', lbl_logo: 'Лого',
    lbl_show: 'Показать', lbl_gp_override: 'Переопр. GP',
    lbl_outlines: 'Контуры', lbl_figure: 'Фигура',
    lbl_starfield: 'Поле', lbl_starfield_mode: 'Режим',
    lbl_permanent_night: 'Пост. ночь', lbl_show_satellites: 'Спутники',
    lbl_specified_tracker_mode: 'Задан. трекер',
    lbl_true_positions: 'Истин. позиции',
    lbl_gp_path_24h: 'Путь GP (24 ч)',
    lbl_ephemeris_comparison: 'Сравн. эфемерид',
    lbl_precession: 'Прецессия', lbl_nutation: 'Нутация',
    lbl_aberration: 'Аберрация', lbl_trepidation: 'Трепидация',
    lbl_source: 'Источник',
    lbl_observer_lat: 'Шир. набл.', lbl_observer_long: 'Долг. набл.',
    lbl_size: 'Размер', lbl_height: 'Высота',
    lbl_hq_map_art: 'HQ Карта', lbl_generated: 'Сгенер.',
    lbl_camera_dir: 'Направл.', lbl_camera_height: 'Высота',
    lbl_camera_dist: 'Расстояние', lbl_zoom: 'Зум',
    lbl_elevation: 'Высота',
    lbl_vault_size: 'Размер свода', lbl_vault_height: 'Высота свода',
    lbl_day_of_year: 'День года', lbl_time: 'Время',
    lbl_datetime: 'Дата-время', lbl_timezone: 'Часовой пояс',
    lbl_date_time_field: 'Дата / время',
    lbl_speed: 'Скорость',
    btn_pause: 'Пауза', btn_play: 'Воспр.',
    status_running: 'идёт', status_paused: 'на паузе',
    btn_day: 'День', btn_week: 'Нед.', btn_month: 'Мес.', btn_year: 'Год',
    app_title: 'КОНЦЕПТ. МОДЕЛЬ FE',
    app_subtitle: 'Модульный порт. Все расстояния безразмерные (радиус FE = 1).',
    panel_live_moon_phases: 'Фазы Луны в прямом эфире',
    panel_live_ephemeris_data: 'Эфемериды в прямом эфире',
    beyond_vault: 'за пределами оптического свода наблюдателя',
    within_vault: 'внутри оптического свода наблюдателя',
    twilight_civil: 'гражданские сумерки', twilight_nautical: 'навигационные сумерки',
    twilight_astronomical: 'астрономические сумерки',
    daylight: 'дневной свет', night: 'ночь',
    sun_never_leaves: '(Солнце не покидает оптический свод)',
    sun_never_enters: '(Солнце не входит в оптический свод)',
    next_solar_eclipse: 'Следующее солнечное затмение',
    next_lunar_eclipse: 'Следующее лунное затмение',
    about_btn: 'О модели', legend_btn: 'Легенда',
    about_p1: 'Это концептуальная модель. Положения звёзд основаны исключительно на кинематике; оптический свод наблюдателя определяет границу видимости, на которую проецируется звёздное поле.',
    about_p2: 'Любая концептуальная модель, использующая широтно-долготную сетку, не лучше и не хуже любой другой проекции. Сетка внутренне согласована и самореферентна к фиктивному наблюдателю в центре Земли — именно этот фиктивный наблюдатель связывает соотношение 60 NMi / 1° с наземными положениями звёзд.',
    about_p3: '«Фиктивный наблюдатель» не исключает концептуальной модели: каждая такая модель в конечном счёте основана на том, что один реальный наблюдатель видит в собственной полусфере зрения, а сетка — это просто одна бухгалтерская конвенция поверх этого.',
  },

  ar: {
    tab_view: 'عرض', tab_time: 'الوقت', tab_show: 'إظهار',
    tab_tracker: 'متعقب', tab_demos: 'عروض', tab_info: 'معلومات',
    btn_show: 'إظهار', btn_gp_override: 'تجاوز GP',
    btn_enable_all: 'تمكين الكل', btn_disable_all: 'تعطيل الكل',
    btn_disable_satellites: 'تعطيل الأقمار',
    tip_grids: 'تبديل شبكة FE + الشبكة البصرية + درجات السمت',
    tip_map: 'فتح إعدادات الإسقاط',
    tip_starfield: 'تغيير حقل النجوم',
    tip_vault_swap: 'تبديل القبة السماوية ⇄ البصرية',
    tip_rewind: 'إرجاع', tip_play_pause: 'تشغيل / إيقاف',
    tip_fast_forward: 'تقديم سريع',
    tip_slow: 'إبطاء (النصف). يستأنف بهذه السرعة.',
    tip_speed: 'تسريع (الضعف). يستأنف بهذه السرعة.',
    tip_end_demo: 'إيقاف العرض الحالي',
    tip_clear_follow: 'إلغاء التتبع (Esc يعمل أيضاً)',
    tip_permanent_night: 'ليل دائم',
    tip_true_positions: 'علامات المواقع الحقيقية',
    tip_stm: 'وضع المتعقب المحدد',
    tip_tracker_opts_jump: 'الانتقال إلى خيارات المتعقب',
    tip_observer_jump: 'الانتقال إلى المراقب',
    tip_freecam: 'كاميرا حرة — الأسهم تدور الكاميرا',
    tip_az_ring: 'تبديل قراءة البوصلة الكاملة',
    lang_label: 'اللغة',
    grp_observer: 'المراقب', grp_camera: 'كاميرا',
    grp_vault_of_heavens: 'القبة السماوية', grp_optical_vault: 'القبة البصرية',
    grp_body_vaults: 'قبب الأجرام', grp_rays: 'أشعة',
    grp_cosmology: 'علم الكون', grp_map_projection: 'إسقاط الخريطة',
    grp_misc: 'متفرقات', grp_ephemeris: 'تقاويم فلكية',
    grp_starfield: 'حقل النجوم', grp_tracker_options: 'خيارات المتعقب',
    grp_celestial_bodies: 'أجرام سماوية', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'الأبراج', grp_black_holes: 'ثقوب سوداء',
    grp_quasars: 'كوازارات', grp_galaxies: 'مجرات',
    grp_satellites: 'أقمار', grp_bright_star_catalog: 'كتالوج النجوم اللامعة',
    grp_calendar: 'تقويم', grp_autoplay: 'تشغيل تلقائي',
    grp_heavenly_vault: 'القبة السماوية', grp_ground_disc: 'الأرض / القرص',
    grp_date_time: 'التاريخ / الوقت',
    lbl_heavenly_vault: 'قبة', lbl_vault_grid: 'شبكة',
    lbl_sun_track: 'مسار الشمس', lbl_moon_track: 'مسار القمر',
    lbl_optical_vault: 'قبة بصرية', lbl_optical_vault_grid: 'شبكة بصرية',
    lbl_azimuth_ring: 'حلقة سمت', lbl_facing_vector: 'متجه / ش-ج-ش-غ',
    lbl_celestial_poles: 'أقطاب سماوية', lbl_declination_circles: 'دوائر ميل',
    lbl_fe_grid: 'شبكة FE', lbl_tropics_polar: 'مدارات / قطبية',
    lbl_sun_moon_gp: 'GP شمس / قمر', lbl_longitude_ring: 'حلقة طول',
    lbl_shadow: 'ظل',
    lbl_vault_rays: 'أشعة القبة', lbl_optical_vault_rays: 'أشعة بصرية',
    lbl_projection_rays: 'أشعة الإسقاط', lbl_many_rays: 'أشعة كثيرة',
    lbl_axis_mundi: 'محور العالم', lbl_planets: 'كواكب',
    lbl_dark_background: 'خلفية داكنة', lbl_logo: 'شعار',
    lbl_show: 'إظهار', lbl_gp_override: 'تجاوز GP',
    lbl_outlines: 'حدود', lbl_figure: 'شكل',
    lbl_starfield: 'حقل', lbl_starfield_mode: 'وضع',
    lbl_permanent_night: 'ليل دائم', lbl_show_satellites: 'إظهار الأقمار',
    lbl_specified_tracker_mode: 'متعقب محدد',
    lbl_true_positions: 'مواقع حقيقية',
    lbl_gp_path_24h: 'مسار GP (24 س)',
    lbl_ephemeris_comparison: 'مقارنة التقاويم',
    lbl_precession: 'مبادرة', lbl_nutation: 'ترنح',
    lbl_aberration: 'انحراف', lbl_trepidation: 'تذبذب',
    lbl_source: 'المصدر',
    lbl_observer_lat: 'خط عرض المراقب', lbl_observer_long: 'خط طول المراقب',
    lbl_size: 'حجم', lbl_height: 'ارتفاع',
    lbl_hq_map_art: 'خريطة HQ', lbl_generated: 'مُولّد',
    lbl_camera_dir: 'اتجاه', lbl_camera_height: 'ارتفاع',
    lbl_camera_dist: 'مسافة', lbl_zoom: 'تكبير',
    lbl_elevation: 'ارتفاع',
    lbl_vault_size: 'حجم القبة', lbl_vault_height: 'ارتفاع القبة',
    lbl_day_of_year: 'يوم السنة', lbl_time: 'الوقت',
    lbl_datetime: 'التاريخ-الوقت', lbl_timezone: 'المنطقة الزمنية',
    lbl_date_time_field: 'التاريخ / الوقت',
    lbl_speed: 'السرعة',
    btn_pause: 'إيقاف', btn_play: 'تشغيل',
    status_running: 'يعمل', status_paused: 'متوقف',
    btn_day: 'يوم', btn_week: 'أسبوع', btn_month: 'شهر', btn_year: 'سنة',
    app_title: 'النموذج المفاهيمي FE',
    app_subtitle: 'منفذ معياري. جميع المسافات بلا أبعاد (نصف قطر FE = 1).',
    panel_live_moon_phases: 'أطوار القمر المباشرة',
    panel_live_ephemeris_data: 'بيانات التقاويم الفلكية المباشرة',
    beyond_vault: 'خارج القبة البصرية للمراقب',
    within_vault: 'داخل القبة البصرية للمراقب',
    twilight_civil: 'الشفق المدني', twilight_nautical: 'الشفق الملاحي',
    twilight_astronomical: 'الشفق الفلكي',
    daylight: 'ضوء النهار', night: 'ليل',
    sun_never_leaves: '(الشمس لا تغادر القبة البصرية)',
    sun_never_enters: '(الشمس لا تدخل القبة البصرية)',
    next_solar_eclipse: 'كسوف الشمس القادم',
    next_lunar_eclipse: 'خسوف القمر القادم',
    about_btn: 'حول', legend_btn: 'وصف',
    about_p1: 'هذا نموذج مفاهيمي. مواقع النجوم تعتمد بحتاً على الحركية؛ القبة البصرية للمراقب تحدد حد الرؤية الذي يُعرض عليه حقل النجوم.',
    about_p2: 'أي نموذج مفاهيمي يستخدم شبكة خطوط الطول والعرض ليس أفضل ولا أسوأ من أي إسقاط آخر. الشبكة متسقة داخلياً ومرجعية ذاتياً لمراقب وهمي في مركز الأرض — هذا المراقب الوهمي هو الذي يربط علاقة 60 ميل بحري / 1° بمواقع النجوم على الأرض.',
    about_p3: '"المراقب الوهمي" لا يتعارض مع النموذج المفاهيمي: كل نموذج من هذا النوع يستند في النهاية إلى ما يراه مراقب حقيقي واحد في نصف الكرة المرئي الخاص به، والشبكة هي ببساطة اتفاقية محاسبية واحدة فوق ذلك.',
  },

  he: {
    tab_view: 'תצוגה', tab_time: 'זמן', tab_show: 'הצג',
    tab_tracker: 'עוקב', tab_demos: 'הדגמות', tab_info: 'מידע',
    btn_show: 'הצג', btn_gp_override: 'דריסת GP',
    btn_enable_all: 'אפשר הכל', btn_disable_all: 'בטל הכל',
    btn_disable_satellites: 'בטל לוויינים',
    tip_grids: 'החלפת רשת FE + רשת אופטית + מעלות אזימוט',
    tip_map: 'פתח הגדרות הטלה',
    tip_starfield: 'החלף שדה כוכבים',
    tip_vault_swap: 'החלף כיפת השמים ⇄ אופטית',
    tip_rewind: 'אחורה', tip_play_pause: 'נגן / השהה',
    tip_fast_forward: 'הרצה קדימה',
    tip_slow: 'האטה (חצי). ממשיך במהירות זו.',
    tip_speed: 'הגברה (כפול). ממשיך במהירות זו.',
    tip_end_demo: 'עצור הדגמה נוכחית',
    tip_clear_follow: 'נקה מטרה (Esc גם)',
    tip_permanent_night: 'לילה קבוע',
    tip_true_positions: 'סמני מיקומים אמיתיים',
    tip_stm: 'מצב עוקב מוגדר',
    tip_tracker_opts_jump: 'מעבר לאפשרויות העוקב',
    tip_observer_jump: 'מעבר למתבונן',
    tip_freecam: 'מצלמה חופשית — חצים מסובבים את המצלמה',
    tip_az_ring: 'החלפת קריאת מצפן מלאה',
    lang_label: 'שפה',
    grp_observer: 'מתבונן', grp_camera: 'מצלמה',
    grp_vault_of_heavens: 'כיפת השמים', grp_optical_vault: 'כיפה אופטית',
    grp_body_vaults: 'כיפות גופים', grp_rays: 'קרניים',
    grp_cosmology: 'קוסמולוגיה', grp_map_projection: 'הטלת מפה',
    grp_misc: 'שונות', grp_ephemeris: 'אפמרידות',
    grp_starfield: 'שדה כוכבים', grp_tracker_options: 'אפשרויות',
    grp_celestial_bodies: 'גרמי שמים', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'קבוצות כוכבים', grp_black_holes: 'חורים שחורים',
    grp_quasars: 'קוואזרים', grp_galaxies: 'גלקסיות',
    grp_satellites: 'לוויינים', grp_bright_star_catalog: 'קטלוג כוכבים בהירים',
    grp_calendar: 'לוח שנה', grp_autoplay: 'נגינה אוטומטית',
    grp_heavenly_vault: 'כיפת השמים', grp_ground_disc: 'אדמה / דיסק',
    grp_date_time: 'תאריך / שעה',
    lbl_heavenly_vault: 'כיפה', lbl_vault_grid: 'רשת',
    lbl_sun_track: 'נתיב שמש', lbl_moon_track: 'נתיב ירח',
    lbl_optical_vault: 'כיפה אופטית', lbl_optical_vault_grid: 'רשת אופטית',
    lbl_azimuth_ring: 'טבעת אזימוט', lbl_facing_vector: 'וקטור / צ-ד-מ-מע',
    lbl_celestial_poles: 'קטבים שמיימיים', lbl_declination_circles: 'מעגלי נטייה',
    lbl_fe_grid: 'רשת FE', lbl_tropics_polar: 'טרופי / קוטבי',
    lbl_sun_moon_gp: 'GP שמש / ירח', lbl_longitude_ring: 'טבעת אורך',
    lbl_shadow: 'צל',
    lbl_vault_rays: 'קרני כיפה', lbl_optical_vault_rays: 'קרניים אופטיות',
    lbl_projection_rays: 'קרני הטלה', lbl_many_rays: 'הרבה קרניים',
    lbl_axis_mundi: 'ציר עולם', lbl_planets: 'כוכבי לכת',
    lbl_dark_background: 'רקע כהה', lbl_logo: 'לוגו',
    lbl_show: 'הצג', lbl_gp_override: 'דריסת GP',
    lbl_outlines: 'מתאר', lbl_figure: 'דמות',
    lbl_starfield: 'שדה', lbl_starfield_mode: 'מצב',
    lbl_permanent_night: 'לילה קבוע', lbl_show_satellites: 'הצג לוויינים',
    lbl_specified_tracker_mode: 'עוקב מוגדר',
    lbl_true_positions: 'מיקומים אמת',
    lbl_gp_path_24h: 'נתיב GP (24 ש)',
    lbl_ephemeris_comparison: 'השוואת אפמרידות',
    lbl_precession: 'נקיפה', lbl_nutation: 'נדנוד',
    lbl_aberration: 'סטייה', lbl_trepidation: 'רעידה',
    lbl_source: 'מקור',
    lbl_observer_lat: 'רוחב מתב.', lbl_observer_long: 'אורך מתב.',
    lbl_size: 'גודל', lbl_height: 'גובה',
    lbl_hq_map_art: 'מפת HQ', lbl_generated: 'נוצר',
    lbl_camera_dir: 'כיוון', lbl_camera_height: 'גובה',
    lbl_camera_dist: 'מרחק', lbl_zoom: 'זום',
    lbl_elevation: 'גובה',
    lbl_vault_size: 'גודל כיפה', lbl_vault_height: 'גובה כיפה',
    lbl_day_of_year: 'יום בשנה', lbl_time: 'שעה',
    lbl_datetime: 'תאריך-שעה', lbl_timezone: 'אזור זמן',
    lbl_date_time_field: 'תאריך / שעה',
    lbl_speed: 'מהירות',
    btn_pause: 'השהה', btn_play: 'נגן',
    status_running: 'פעיל', status_paused: 'מושהה',
    btn_day: 'יום', btn_week: 'שבוע', btn_month: 'חודש', btn_year: 'שנה',
    app_title: 'מודל מושגי FE',
    app_subtitle: 'יציאה מודולרית. כל המרחקים חסרי ממדים (רדיוס FE = 1).',
    panel_live_moon_phases: 'מופעי הירח בשידור חי',
    panel_live_ephemeris_data: 'נתוני אפמרידות בשידור חי',
    beyond_vault: 'מעבר לכיפה האופטית של המתבונן',
    within_vault: 'בתוך הכיפה האופטית של המתבונן',
    twilight_civil: 'דמדומים אזרחיים', twilight_nautical: 'דמדומים ימיים',
    twilight_astronomical: 'דמדומים אסטרונומיים',
    daylight: 'אור יום', night: 'לילה',
    sun_never_leaves: '(השמש לא עוזבת את הכיפה האופטית)',
    sun_never_enters: '(השמש לא נכנסת לכיפה האופטית)',
    next_solar_eclipse: 'ליקוי חמה הבא',
    next_lunar_eclipse: 'ליקוי ירח הבא',
    about_btn: 'אודות', legend_btn: 'מקרא',
    about_p1: 'זהו מודל מושגי. מיקומי הכוכבים מבוססים אך ורק על קינמטיקה; הכיפה האופטית של המתבונן מגדירה את גבול הראייה שעליו מוקרן שדה הכוכבים.',
    about_p2: 'כל מודל מושגי המשתמש ברשת קווי אורך/רוחב אינו טוב ולא רע יותר מכל הטלה אחרת. הרשת עקבית פנימית ועצמית-התייחסות למתבונן בדיוני במרכז הארץ — זה אותו מתבונן בדיוני שקושר את היחס 60 NMi / 1° למיקומי הכוכבים על הקרקע.',
    about_p3: '"מתבונן בדיוני" אינו סותר את המודל המושגי: כל מודל כזה מעוגן בסופו של דבר במה שמתבונן אמיתי אחד רואה בחצי הכדור הראייתי שלו, והרשת היא פשוט מוסכמה ראשית אחת מעל זה.',
  },

  zh: {
    tab_view: '视图', tab_time: '时间', tab_show: '显示',
    tab_tracker: '追踪', tab_demos: '演示', tab_info: '信息',
    btn_show: '显示', btn_gp_override: '覆盖 GP',
    btn_enable_all: '全部启用', btn_disable_all: '全部禁用',
    btn_disable_satellites: '禁用卫星',
    tip_grids: '切换 FE 网格 + 光学网格 + 方位角度',
    tip_map: '打开地图投影设置',
    tip_starfield: '切换星场',
    tip_vault_swap: '切换天穹 ⇄ 光学穹',
    tip_rewind: '后退', tip_play_pause: '播放 / 暂停',
    tip_fast_forward: '快进',
    tip_slow: '减慢（一半）。继续以此速度。',
    tip_speed: '加速（双倍）。继续以此速度。',
    tip_end_demo: '停止当前演示',
    tip_clear_follow: '清除目标（也可按 Esc）',
    tip_permanent_night: '永久夜晚',
    tip_true_positions: '真实位置标记',
    tip_stm: '指定追踪模式',
    tip_tracker_opts_jump: '跳转到追踪选项',
    tip_observer_jump: '跳转到观察者',
    tip_freecam: '自由相机 — 箭头旋转相机',
    tip_az_ring: '切换完整罗盘读数',
    lang_label: '语言',
    grp_observer: '观察者', grp_camera: '相机',
    grp_vault_of_heavens: '天穹', grp_optical_vault: '光学穹',
    grp_body_vaults: '天体穹', grp_rays: '射线',
    grp_cosmology: '宇宙学', grp_map_projection: '地图投影',
    grp_misc: '杂项', grp_ephemeris: '星历',
    grp_starfield: '星场', grp_tracker_options: '追踪选项',
    grp_celestial_bodies: '天体', grp_cel_nav: 'Cel Nav',
    grp_constellations: '星座', grp_black_holes: '黑洞',
    grp_quasars: '类星体', grp_galaxies: '星系',
    grp_satellites: '卫星', grp_bright_star_catalog: '亮星目录',
    grp_calendar: '日历', grp_autoplay: '自动播放',
    grp_heavenly_vault: '天穹', grp_ground_disc: '地面 / 圆盘',
    grp_date_time: '日期 / 时间',
    lbl_heavenly_vault: '天穹', lbl_vault_grid: '网格',
    lbl_sun_track: '太阳轨迹', lbl_moon_track: '月亮轨迹',
    lbl_optical_vault: '光学穹', lbl_optical_vault_grid: '光学网格',
    lbl_azimuth_ring: '方位环', lbl_facing_vector: '矢量 / 北南东西',
    lbl_celestial_poles: '天极', lbl_declination_circles: '赤纬圈',
    lbl_fe_grid: 'FE 网格', lbl_tropics_polar: '回归 / 极',
    lbl_sun_moon_gp: '日月 GP', lbl_longitude_ring: '经度环',
    lbl_shadow: '阴影',
    lbl_vault_rays: '穹射线', lbl_optical_vault_rays: '光学射线',
    lbl_projection_rays: '投影射线', lbl_many_rays: '多射线',
    lbl_axis_mundi: '世界轴', lbl_planets: '行星',
    lbl_dark_background: '暗背景', lbl_logo: '标志',
    lbl_show: '显示', lbl_gp_override: '覆盖 GP',
    lbl_outlines: '轮廓', lbl_figure: '图形',
    lbl_starfield: '星场', lbl_starfield_mode: '模式',
    lbl_permanent_night: '永久夜', lbl_show_satellites: '显示卫星',
    lbl_specified_tracker_mode: '指定追踪',
    lbl_true_positions: '真实位置',
    lbl_gp_path_24h: 'GP 路径（24 h）',
    lbl_ephemeris_comparison: '星历比较',
    lbl_precession: '岁差', lbl_nutation: '章动',
    lbl_aberration: '光行差', lbl_trepidation: '颤动',
    lbl_source: '源',
    lbl_observer_lat: '观察纬度', lbl_observer_long: '观察经度',
    lbl_size: '大小', lbl_height: '高度',
    lbl_hq_map_art: 'HQ 地图', lbl_generated: '生成',
    lbl_camera_dir: '方向', lbl_camera_height: '高度',
    lbl_camera_dist: '距离', lbl_zoom: '缩放',
    lbl_elevation: '仰角',
    lbl_vault_size: '穹大小', lbl_vault_height: '穹高度',
    lbl_day_of_year: '年中第几日', lbl_time: '时间',
    lbl_datetime: '日期时间', lbl_timezone: '时区',
    lbl_date_time_field: '日期 / 时间',
    lbl_speed: '速度',
    btn_pause: '暂停', btn_play: '播放',
    status_running: '运行中', status_paused: '已暂停',
    btn_day: '日', btn_week: '周', btn_month: '月', btn_year: '年',
    app_title: 'FE 概念模型',
    app_subtitle: '模块化端口。所有距离均为无量纲（FE 半径 = 1）。',
    panel_live_moon_phases: '实时月相',
    panel_live_ephemeris_data: '实时星历数据',
    beyond_vault: '在观察者光学穹外',
    within_vault: '在观察者光学穹内',
    twilight_civil: '民用曙暮光', twilight_nautical: '航海曙暮光',
    twilight_astronomical: '天文曙暮光',
    daylight: '日光', night: '夜',
    sun_never_leaves: '（太阳从不离开光学穹）',
    sun_never_enters: '（太阳从不进入光学穹）',
    next_solar_eclipse: '下次日食',
    next_lunar_eclipse: '下次月食',
    about_btn: '关于', legend_btn: '图例',
    about_p1: '这是一个概念模型。星星的位置纯粹基于运动学；观察者的光学穹定义了视野的极限，星场就投影在其上。',
    about_p2: '任何使用经纬度网格的概念模型并不比任何其他投影更好或更差。该网格内部一致并自指于地球中心的虚拟观察者——正是这位虚拟观察者将 60 NMi / 1° 关系绑定到星星的地面位置。',
    about_p3: '"虚拟观察者" 并不与概念模型互斥：每个这样的模型最终都根植于单个真实观察者在自己的视觉半球中所见，而网格只是其上的一个记账约定。',
  },

  ja: {
    tab_view: '表示', tab_time: '時間', tab_show: '表示',
    tab_tracker: 'トラッカー', tab_demos: 'デモ', tab_info: '情報',
    btn_show: '表示', btn_gp_override: 'GP上書き',
    btn_enable_all: 'すべて有効', btn_disable_all: 'すべて無効',
    btn_disable_satellites: '衛星を無効',
    tip_grids: 'FEグリッド+光学穹+方位角を切替',
    tip_map: '地図投影設定を開く',
    tip_starfield: '星野を切替',
    tip_vault_swap: '天穹⇄光学穹を切替',
    tip_rewind: '巻戻し', tip_play_pause: '再生 / 一時停止',
    tip_fast_forward: '早送り',
    tip_slow: '減速（半分）。この速度で再開。',
    tip_speed: '加速（倍）。この速度で再開。',
    tip_end_demo: '現在のデモを停止',
    tip_clear_follow: '追跡解除（Esc も）',
    tip_permanent_night: '永続夜',
    tip_true_positions: '真位置マーカー',
    tip_stm: '指定トラッカーモード',
    tip_tracker_opts_jump: 'トラッカーオプションへ',
    tip_observer_jump: '観察者へ',
    tip_freecam: 'フリーカメラ — 矢印で回転',
    tip_az_ring: 'コンパス完全表示の切替',
    lang_label: '言語',
    grp_observer: '観察者', grp_camera: 'カメラ',
    grp_vault_of_heavens: '天穹', grp_optical_vault: '光学穹',
    grp_body_vaults: '天体穹', grp_rays: '光線',
    grp_cosmology: '宇宙論', grp_map_projection: '地図投影',
    grp_misc: 'その他', grp_ephemeris: '暦表',
    grp_starfield: '星野', grp_tracker_options: 'オプション',
    grp_celestial_bodies: '天体', grp_cel_nav: 'Cel Nav',
    grp_constellations: '星座', grp_black_holes: 'ブラックホール',
    grp_quasars: 'クェーサー', grp_galaxies: '銀河',
    grp_satellites: '衛星', grp_bright_star_catalog: '明るい星カタログ',
    grp_calendar: 'カレンダー', grp_autoplay: '自動再生',
    grp_heavenly_vault: '天穹', grp_ground_disc: '地表 / ディスク',
    grp_date_time: '日付 / 時刻',
    lbl_heavenly_vault: '天穹', lbl_vault_grid: 'グリッド',
    lbl_sun_track: '太陽軌跡', lbl_moon_track: '月軌跡',
    lbl_optical_vault: '光学穹', lbl_optical_vault_grid: '光学グリッド',
    lbl_azimuth_ring: '方位環', lbl_facing_vector: 'ベクトル / 北南東西',
    lbl_celestial_poles: '天極', lbl_declination_circles: '赤緯円',
    lbl_fe_grid: 'FEグリッド', lbl_tropics_polar: '回帰線 / 極',
    lbl_sun_moon_gp: '日月GP', lbl_longitude_ring: '経度環',
    lbl_shadow: '影',
    lbl_vault_rays: '穹光線', lbl_optical_vault_rays: '光学光線',
    lbl_projection_rays: '投影光線', lbl_many_rays: '多光線',
    lbl_axis_mundi: '世界軸', lbl_planets: '惑星',
    lbl_dark_background: '暗背景', lbl_logo: 'ロゴ',
    lbl_show: '表示', lbl_gp_override: 'GP上書き',
    lbl_outlines: '輪郭', lbl_figure: '形',
    lbl_starfield: '星野', lbl_starfield_mode: 'モード',
    lbl_permanent_night: '永続夜', lbl_show_satellites: '衛星表示',
    lbl_specified_tracker_mode: '指定トラッカー',
    lbl_true_positions: '真位置',
    lbl_gp_path_24h: 'GP軌跡（24時間）',
    lbl_ephemeris_comparison: '暦表比較',
    lbl_precession: '歳差', lbl_nutation: '章動',
    lbl_aberration: '光行差', lbl_trepidation: '振動',
    lbl_source: '源',
    lbl_observer_lat: '観察緯度', lbl_observer_long: '観察経度',
    lbl_size: 'サイズ', lbl_height: '高さ',
    lbl_hq_map_art: 'HQマップ', lbl_generated: '生成',
    lbl_camera_dir: '方向', lbl_camera_height: '高さ',
    lbl_camera_dist: '距離', lbl_zoom: 'ズーム',
    lbl_elevation: '仰角',
    lbl_vault_size: '穹サイズ', lbl_vault_height: '穹高さ',
    lbl_day_of_year: '年通日', lbl_time: '時刻',
    lbl_datetime: '日時', lbl_timezone: 'タイムゾーン',
    lbl_date_time_field: '日付 / 時刻',
    lbl_speed: '速度',
    btn_pause: '一時停止', btn_play: '再生',
    status_running: '実行中', status_paused: '一時停止中',
    btn_day: '日', btn_week: '週', btn_month: '月', btn_year: '年',
    app_title: 'FE概念モデル',
    app_subtitle: 'モジュラーポート。すべての距離は無次元（FE半径 = 1）。',
    panel_live_moon_phases: 'ライブ月相',
    panel_live_ephemeris_data: 'ライブ暦表データ',
    beyond_vault: '観察者の光学穹の外',
    within_vault: '観察者の光学穹の内',
    twilight_civil: '市民薄明', twilight_nautical: '航海薄明',
    twilight_astronomical: '天文薄明',
    daylight: '日光', night: '夜',
    sun_never_leaves: '（太陽は光学穹を離れない）',
    sun_never_enters: '（太陽は光学穹に入らない）',
    next_solar_eclipse: '次の日食',
    next_lunar_eclipse: '次の月食',
    about_btn: '概要', legend_btn: '凡例',
    about_p1: 'これは概念モデルです。星の位置は純粋に運動学に基づいており、観察者の光学穹が星野が投影される視野の限界を定義します。',
    about_p2: '緯度経度のグラティキュールを使う概念モデルは、他のどの投影よりも優れたものでも劣ったものでもありません。グラティキュールは地球中心の架空の観察者に対して内部的に一貫し自己参照的です — その架空の観察者が 60 NMi / 1° の関係を星の地上位置に結びつけます。',
    about_p3: '「架空の観察者」は概念モデルと相互排他的ではありません: そのようなすべてのモデルは、最終的に一人の現実の観察者が自分の視野半球で見るものに根ざしており、グラティキュールはその上の一つの会計上の慣習に過ぎません。',
  },

  ko: {
    tab_view: '보기', tab_time: '시간', tab_show: '표시',
    tab_tracker: '추적기', tab_demos: '데모', tab_info: '정보',
    btn_show: '표시', btn_gp_override: 'GP 재정의',
    btn_enable_all: '모두 활성화', btn_disable_all: '모두 비활성화',
    btn_disable_satellites: '위성 비활성화',
    tip_grids: 'FE 격자 + 광학 격자 + 방위각 전환',
    tip_map: '지도 투영 설정 열기',
    tip_starfield: '별자리장 전환',
    tip_vault_swap: '천구 ⇄ 광학 궁륭 전환',
    tip_rewind: '되감기', tip_play_pause: '재생 / 일시정지',
    tip_fast_forward: '빨리감기',
    tip_slow: '감속(절반). 이 속도로 재개.',
    tip_speed: '가속(두 배). 이 속도로 재개.',
    tip_end_demo: '현재 데모 중지',
    tip_clear_follow: '추적 해제(Esc도 가능)',
    tip_permanent_night: '영구 밤',
    tip_true_positions: '실제 위치 표시',
    tip_stm: '지정 추적 모드',
    tip_tracker_opts_jump: '추적기 옵션으로',
    tip_observer_jump: '관측자로',
    tip_freecam: '자유 카메라 — 화살표로 회전',
    tip_az_ring: '전체 나침반 표시 전환',
    lang_label: '언어',
    grp_observer: '관측자', grp_camera: '카메라',
    grp_vault_of_heavens: '천구', grp_optical_vault: '광학 궁륭',
    grp_body_vaults: '천체 궁륭', grp_rays: '광선',
    grp_cosmology: '우주론', grp_map_projection: '지도 투영',
    grp_misc: '기타', grp_ephemeris: '천문력',
    grp_starfield: '별자리장', grp_tracker_options: '옵션',
    grp_celestial_bodies: '천체', grp_cel_nav: 'Cel Nav',
    grp_constellations: '별자리', grp_black_holes: '블랙홀',
    grp_quasars: '퀘이사', grp_galaxies: '은하',
    grp_satellites: '위성', grp_bright_star_catalog: '밝은 별 카탈로그',
    grp_calendar: '달력', grp_autoplay: '자동 재생',
    grp_heavenly_vault: '천구', grp_ground_disc: '지면 / 디스크',
    grp_date_time: '날짜 / 시간',
    lbl_heavenly_vault: '천구', lbl_vault_grid: '격자',
    lbl_sun_track: '태양 궤적', lbl_moon_track: '달 궤적',
    lbl_optical_vault: '광학 궁륭', lbl_optical_vault_grid: '광학 격자',
    lbl_azimuth_ring: '방위환', lbl_facing_vector: '벡터 / 북남동서',
    lbl_celestial_poles: '천극', lbl_declination_circles: '적위원',
    lbl_fe_grid: 'FE 격자', lbl_tropics_polar: '회귀선 / 극',
    lbl_sun_moon_gp: '해/달 GP', lbl_longitude_ring: '경도환',
    lbl_shadow: '그림자',
    lbl_vault_rays: '궁륭광선', lbl_optical_vault_rays: '광학광선',
    lbl_projection_rays: '투영광선', lbl_many_rays: '많은 광선',
    lbl_axis_mundi: '세계축', lbl_planets: '행성',
    lbl_dark_background: '어두운 배경', lbl_logo: '로고',
    lbl_show: '표시', lbl_gp_override: 'GP 재정의',
    lbl_outlines: '윤곽', lbl_figure: '모양',
    lbl_starfield: '별자리장', lbl_starfield_mode: '모드',
    lbl_permanent_night: '영구 밤', lbl_show_satellites: '위성 표시',
    lbl_specified_tracker_mode: '지정 추적',
    lbl_true_positions: '실제 위치',
    lbl_gp_path_24h: 'GP 경로(24h)',
    lbl_ephemeris_comparison: '천문력 비교',
    lbl_precession: '세차', lbl_nutation: '장동',
    lbl_aberration: '광행차', lbl_trepidation: '진동',
    lbl_source: '소스',
    lbl_observer_lat: '관측 위도', lbl_observer_long: '관측 경도',
    lbl_size: '크기', lbl_height: '높이',
    lbl_hq_map_art: 'HQ 지도', lbl_generated: '생성',
    lbl_camera_dir: '방향', lbl_camera_height: '높이',
    lbl_camera_dist: '거리', lbl_zoom: '확대',
    lbl_elevation: '고도',
    lbl_vault_size: '궁륭 크기', lbl_vault_height: '궁륭 높이',
    lbl_day_of_year: '연중 일자', lbl_time: '시간',
    lbl_datetime: '날짜시간', lbl_timezone: '시간대',
    lbl_date_time_field: '날짜 / 시간',
    lbl_speed: '속도',
    btn_pause: '일시정지', btn_play: '재생',
    status_running: '실행 중', status_paused: '일시정지됨',
    btn_day: '일', btn_week: '주', btn_month: '월', btn_year: '년',
    app_title: 'FE 개념 모델',
    app_subtitle: '모듈러 포트. 모든 거리는 무차원(FE 반경 = 1).',
    panel_live_moon_phases: '실시간 달 위상',
    panel_live_ephemeris_data: '실시간 천문력 데이터',
    beyond_vault: '관측자의 광학 궁륭 너머',
    within_vault: '관측자의 광학 궁륭 안',
    twilight_civil: '시민 박명', twilight_nautical: '항해 박명',
    twilight_astronomical: '천문 박명',
    daylight: '햇빛', night: '밤',
    sun_never_leaves: '(태양이 광학 궁륭을 떠나지 않음)',
    sun_never_enters: '(태양이 광학 궁륭에 들어오지 않음)',
    next_solar_eclipse: '다음 일식',
    next_lunar_eclipse: '다음 월식',
    about_btn: '정보', legend_btn: '범례',
    about_p1: '이것은 개념적 모델입니다. 별의 위치는 순수하게 운동학에 기반합니다; 관측자의 광학 궁륭이 별자리장이 투영되는 시야의 한계를 정의합니다.',
    about_p2: '위도/경도 격자를 사용하는 어떤 개념적 모델도 다른 투영보다 더 좋거나 나쁘지 않습니다. 격자는 지구 중심의 가상 관측자에 대해 내부적으로 일관되고 자기 참조적입니다 — 그 가상 관측자가 60 NMi / 1° 관계를 별의 지상 위치에 연결합니다.',
    about_p3: '"가상 관측자"는 개념적 모델과 상호 배타적이지 않습니다: 모든 그러한 모델은 결국 한 명의 실제 관측자가 자신의 시각 반구에서 보는 것에 뿌리를 두고 있으며, 격자는 그 위에 있는 하나의 회계 규약일 뿐입니다.',
  },

  th: {
    tab_view: 'มุมมอง', tab_time: 'เวลา', tab_show: 'แสดง',
    tab_tracker: 'ตัวติดตาม', tab_demos: 'เดโม', tab_info: 'ข้อมูล',
    btn_show: 'แสดง', btn_gp_override: 'แทนที่ GP',
    btn_enable_all: 'เปิดทั้งหมด', btn_disable_all: 'ปิดทั้งหมด',
    btn_disable_satellites: 'ปิดดาวเทียม',
    tip_grids: 'สลับกริด FE + กริดแสง + องศาแอซิมุท',
    tip_map: 'เปิดการตั้งค่าการฉาย',
    tip_starfield: 'สลับสนามดาว',
    tip_vault_swap: 'สลับโดมท้องฟ้า ⇄ ทัศนศาสตร์',
    tip_rewind: 'กรอกลับ', tip_play_pause: 'เล่น / หยุด',
    tip_fast_forward: 'กรอไปข้างหน้า',
    tip_slow: 'ช้าลง (ครึ่ง). ดำเนินต่อที่ความเร็วนี้.',
    tip_speed: 'เร็วขึ้น (สองเท่า). ดำเนินต่อที่ความเร็วนี้.',
    tip_end_demo: 'หยุดเดโมปัจจุบัน',
    tip_clear_follow: 'ล้างเป้าหมาย (Esc ก็ได้)',
    tip_permanent_night: 'กลางคืนถาวร',
    tip_true_positions: 'เครื่องหมายตำแหน่งจริง',
    tip_stm: 'โหมดตัวติดตามที่ระบุ',
    tip_tracker_opts_jump: 'ไปยังตัวเลือกตัวติดตาม',
    tip_observer_jump: 'ไปยังผู้สังเกต',
    tip_freecam: 'กล้องอิสระ — ลูกศรหมุนกล้อง',
    tip_az_ring: 'สลับการอ่านเข็มทิศเต็ม',
    lang_label: 'ภาษา',
    grp_observer: 'ผู้สังเกต', grp_camera: 'กล้อง',
    grp_vault_of_heavens: 'โดมท้องฟ้า', grp_optical_vault: 'โดมทัศนศาสตร์',
    grp_body_vaults: 'โดมเทห์ฟ้า', grp_rays: 'รังสี',
    grp_cosmology: 'จักรวาลวิทยา', grp_map_projection: 'การฉายแผนที่',
    grp_misc: 'อื่นๆ', grp_ephemeris: 'อีเฟเมริส',
    grp_starfield: 'สนามดาว', grp_tracker_options: 'ตัวเลือก',
    grp_celestial_bodies: 'เทห์ฟ้า', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'กลุ่มดาว', grp_black_holes: 'หลุมดำ',
    grp_quasars: 'ควาซาร์', grp_galaxies: 'กาแล็กซี',
    grp_satellites: 'ดาวเทียม', grp_bright_star_catalog: 'แคตตาล็อกดาวสว่าง',
    grp_calendar: 'ปฏิทิน', grp_autoplay: 'เล่นอัตโนมัติ',
    grp_heavenly_vault: 'โดมท้องฟ้า', grp_ground_disc: 'พื้น / จาน',
    grp_date_time: 'วันที่ / เวลา',
    lbl_heavenly_vault: 'โดม', lbl_vault_grid: 'กริด',
    lbl_sun_track: 'เส้นทางอาทิตย์', lbl_moon_track: 'เส้นทางจันทร์',
    lbl_optical_vault: 'โดมแสง', lbl_optical_vault_grid: 'กริดแสง',
    lbl_azimuth_ring: 'วงแอซิมุท', lbl_facing_vector: 'เวกเตอร์ / N-S-E-W',
    lbl_celestial_poles: 'ขั้วฟ้า', lbl_declination_circles: 'วงค่ามุมเอียง',
    lbl_fe_grid: 'กริด FE', lbl_tropics_polar: 'เขตร้อน / ขั้ว',
    lbl_sun_moon_gp: 'GP อาทิตย์/จันทร์', lbl_longitude_ring: 'วงลองจิจูด',
    lbl_shadow: 'เงา',
    lbl_vault_rays: 'รังสีโดม', lbl_optical_vault_rays: 'รังสีแสง',
    lbl_projection_rays: 'รังสีฉาย', lbl_many_rays: 'หลายรังสี',
    lbl_axis_mundi: 'แกนโลก', lbl_planets: 'ดาวเคราะห์',
    lbl_dark_background: 'พื้นหลังมืด', lbl_logo: 'โลโก้',
    lbl_show: 'แสดง', lbl_gp_override: 'แทนที่ GP',
    lbl_outlines: 'เส้นกรอบ', lbl_figure: 'รูป',
    lbl_starfield: 'สนาม', lbl_starfield_mode: 'โหมด',
    lbl_permanent_night: 'กลางคืนถาวร', lbl_show_satellites: 'แสดงดาวเทียม',
    lbl_specified_tracker_mode: 'ตัวติดตามที่ระบุ',
    lbl_true_positions: 'ตำแหน่งจริง',
    lbl_gp_path_24h: 'เส้นทาง GP (24 ชม.)',
    lbl_ephemeris_comparison: 'เทียบอีเฟเมริส',
    lbl_precession: 'พรีเซสชัน', lbl_nutation: 'นิวเทชัน',
    lbl_aberration: 'อะเบอร์เรชัน', lbl_trepidation: 'การสั่น',
    lbl_source: 'แหล่งที่มา',
    lbl_observer_lat: 'ละติจูดผู้สังเกต', lbl_observer_long: 'ลองจิจูดผู้สังเกต',
    lbl_size: 'ขนาด', lbl_height: 'ความสูง',
    lbl_hq_map_art: 'แผนที่ HQ', lbl_generated: 'สร้างขึ้น',
    lbl_camera_dir: 'ทิศทาง', lbl_camera_height: 'สูง',
    lbl_camera_dist: 'ระยะ', lbl_zoom: 'ซูม',
    lbl_elevation: 'มุมเงย',
    lbl_vault_size: 'ขนาดโดม', lbl_vault_height: 'สูงโดม',
    lbl_day_of_year: 'วันที่ในปี', lbl_time: 'เวลา',
    lbl_datetime: 'วันเวลา', lbl_timezone: 'เขตเวลา',
    lbl_date_time_field: 'วันที่ / เวลา',
    lbl_speed: 'ความเร็ว',
    btn_pause: 'หยุด', btn_play: 'เล่น',
    status_running: 'กำลังเล่น', status_paused: 'หยุดชั่วคราว',
    btn_day: 'วัน', btn_week: 'สัปดาห์', btn_month: 'เดือน', btn_year: 'ปี',
    app_title: 'แบบจำลองแนวคิด FE',
    app_subtitle: 'พอร์ตโมดูลาร์ ระยะทั้งหมดไร้มิติ (รัศมี FE = 1).',
    panel_live_moon_phases: 'เฟสจันทร์สด',
    panel_live_ephemeris_data: 'ข้อมูลอีเฟเมริสสด',
    beyond_vault: 'นอกโดมแสงของผู้สังเกต',
    within_vault: 'ในโดมแสงของผู้สังเกต',
    twilight_civil: 'สนธยาพลเรือน', twilight_nautical: 'สนธยานาวี',
    twilight_astronomical: 'สนธยาดาราศาสตร์',
    daylight: 'แสงแดด', night: 'กลางคืน',
    sun_never_leaves: '(อาทิตย์ไม่ออกจากโดมแสง)',
    sun_never_enters: '(อาทิตย์ไม่เข้าโดมแสง)',
    next_solar_eclipse: 'สุริยุปราคาครั้งถัดไป',
    next_lunar_eclipse: 'จันทรุปราคาครั้งถัดไป',
    about_btn: 'เกี่ยวกับ', legend_btn: 'คำอธิบาย',
    about_p1: 'นี่คือแบบจำลองแนวคิด ตำแหน่งของดาวอิงเฉพาะกับจลนศาสตร์ โดมแสงของผู้สังเกตกำหนดขอบเขตการมองเห็นที่สนามดาวถูกฉาย',
    about_p2: 'แบบจำลองแนวคิดใดที่ใช้ตารางละติจูด/ลองจิจูดไม่ดีกว่าและไม่แย่กว่าการฉายอื่น ตารางมีความสอดคล้องภายในและอ้างอิงตนเองกับผู้สังเกตสมมติที่ศูนย์กลางโลก — ผู้สังเกตสมมตินั้นเชื่อมความสัมพันธ์ 60 NMi / 1° กับตำแหน่งของดาวบนพื้น',
    about_p3: '"ผู้สังเกตสมมติ" ไม่ได้แยกออกจากแบบจำลองแนวคิด: แบบจำลองทุกประเภทดังกล่าวสุดท้ายตั้งอยู่บนสิ่งที่ผู้สังเกตจริงคนหนึ่งเห็นในซีกการมองเห็นของตน และตารางเป็นเพียงข้อตกลงทางบัญชีหนึ่งบนนั้น',
  },

  hi: {
    tab_view: 'दृश्य', tab_time: 'समय', tab_show: 'दिखाएँ',
    tab_tracker: 'ट्रैकर', tab_demos: 'डेमो', tab_info: 'जानकारी',
    btn_show: 'दिखाएँ', btn_gp_override: 'GP अधिरोहण',
    btn_enable_all: 'सब सक्षम', btn_disable_all: 'सब अक्षम',
    btn_disable_satellites: 'उपग्रह अक्षम',
    tip_grids: 'FE जाल + ऑप्टिकल जाल + दिगंश डिग्री टॉगल',
    tip_map: 'मानचित्र प्रक्षेपण सेटिंग खोलें',
    tip_starfield: 'तारा क्षेत्र बदलें',
    tip_vault_swap: 'आकाशीय ⇄ ऑप्टिकल मेहराब बदलें',
    tip_rewind: 'पीछे', tip_play_pause: 'चलाएँ / रोकें',
    tip_fast_forward: 'तेज आगे',
    tip_slow: 'धीमा (आधा). इसी गति से जारी रखें.',
    tip_speed: 'तेज (दोगुना). इसी गति से जारी रखें.',
    tip_end_demo: 'वर्तमान डेमो रोकें',
    tip_clear_follow: 'लक्ष्य हटाएँ (Esc भी)',
    tip_permanent_night: 'स्थायी रात',
    tip_true_positions: 'वास्तविक स्थान चिह्न',
    tip_stm: 'निर्दिष्ट ट्रैकर मोड',
    tip_tracker_opts_jump: 'ट्रैकर विकल्प पर जाएँ',
    tip_observer_jump: 'पर्यवेक्षक पर जाएँ',
    tip_freecam: 'मुक्त कैमरा — तीर कैमरा घुमाते हैं',
    tip_az_ring: 'पूर्ण कम्पास पठन टॉगल',
    lang_label: 'भाषा',
    grp_observer: 'पर्यवेक्षक', grp_camera: 'कैमरा',
    grp_vault_of_heavens: 'आकाशीय मेहराब', grp_optical_vault: 'ऑप्टिकल मेहराब',
    grp_body_vaults: 'पिंड मेहराब', grp_rays: 'किरणें',
    grp_cosmology: 'ब्रह्माण्डविज्ञान', grp_map_projection: 'प्रक्षेपण',
    grp_misc: 'अन्य', grp_ephemeris: 'पंचांग',
    grp_starfield: 'तारा क्षेत्र', grp_tracker_options: 'विकल्प',
    grp_celestial_bodies: 'आकाशीय पिंड', grp_cel_nav: 'Cel Nav',
    grp_constellations: 'तारामंडल', grp_black_holes: 'कृष्ण विवर',
    grp_quasars: 'क्वासर', grp_galaxies: 'आकाशगंगा',
    grp_satellites: 'उपग्रह', grp_bright_star_catalog: 'चमकीले तारे कैटलॉग',
    grp_calendar: 'कैलेंडर', grp_autoplay: 'स्वतः चलाना',
    grp_heavenly_vault: 'आकाशीय मेहराब', grp_ground_disc: 'भूमि / डिस्क',
    grp_date_time: 'दिनांक / समय',
    lbl_heavenly_vault: 'मेहराब', lbl_vault_grid: 'जाल',
    lbl_sun_track: 'सूर्य पथ', lbl_moon_track: 'चंद्र पथ',
    lbl_optical_vault: 'ऑप्टिकल मेहराब', lbl_optical_vault_grid: 'ऑप्टिकल जाल',
    lbl_azimuth_ring: 'दिगंश वलय', lbl_facing_vector: 'सदिश / उत्तर-दक्षिण-पूर्व-पश्चिम',
    lbl_celestial_poles: 'आकाशीय ध्रुव', lbl_declination_circles: 'क्रांति वृत्त',
    lbl_fe_grid: 'FE जाल', lbl_tropics_polar: 'कर्क/मकर / ध्रुवीय',
    lbl_sun_moon_gp: 'सूर्य/चंद्र GP', lbl_longitude_ring: 'देशांतर वलय',
    lbl_shadow: 'छाया',
    lbl_vault_rays: 'मेहराब किरणें', lbl_optical_vault_rays: 'ऑप्टिकल किरणें',
    lbl_projection_rays: 'प्रक्षेपण किरणें', lbl_many_rays: 'कई किरणें',
    lbl_axis_mundi: 'विश्वाक्ष', lbl_planets: 'ग्रह',
    lbl_dark_background: 'गहरी पृष्ठभूमि', lbl_logo: 'लोगो',
    lbl_show: 'दिखाएँ', lbl_gp_override: 'GP अधिरोहण',
    lbl_outlines: 'रूपरेखा', lbl_figure: 'आकृति',
    lbl_starfield: 'क्षेत्र', lbl_starfield_mode: 'मोड',
    lbl_permanent_night: 'स्थायी रात', lbl_show_satellites: 'उपग्रह दिखाएँ',
    lbl_specified_tracker_mode: 'निर्दिष्ट ट्रैकर',
    lbl_true_positions: 'वास्तविक स्थान',
    lbl_gp_path_24h: 'GP पथ (24 घ)',
    lbl_ephemeris_comparison: 'पंचांग तुलना',
    lbl_precession: 'अयन', lbl_nutation: 'शीर्षदोलन',
    lbl_aberration: 'विपथन', lbl_trepidation: 'कंपन',
    lbl_source: 'स्रोत',
    lbl_observer_lat: 'पर्य. अक्षांश', lbl_observer_long: 'पर्य. देशांतर',
    lbl_size: 'आकार', lbl_height: 'ऊँचाई',
    lbl_hq_map_art: 'HQ मानचित्र', lbl_generated: 'जनित',
    lbl_camera_dir: 'दिशा', lbl_camera_height: 'ऊँचाई',
    lbl_camera_dist: 'दूरी', lbl_zoom: 'ज़ूम',
    lbl_elevation: 'उन्नतांश',
    lbl_vault_size: 'मेहराब आकार', lbl_vault_height: 'मेहराब ऊँचाई',
    lbl_day_of_year: 'वर्ष का दिन', lbl_time: 'समय',
    lbl_datetime: 'दिनांक-समय', lbl_timezone: 'समय क्षेत्र',
    lbl_date_time_field: 'दिनांक / समय',
    lbl_speed: 'गति',
    btn_pause: 'रोकें', btn_play: 'चलाएँ',
    status_running: 'चल रहा', status_paused: 'रुका हुआ',
    btn_day: 'दिन', btn_week: 'सप्ताह', btn_month: 'माह', btn_year: 'वर्ष',
    app_title: 'FE संकल्पनात्मक मॉडल',
    app_subtitle: 'मॉड्यूलर पोर्ट. सभी दूरियाँ निर्बाध हैं (FE त्रिज्या = 1).',
    panel_live_moon_phases: 'लाइव चंद्र कलाएँ',
    panel_live_ephemeris_data: 'लाइव पंचांग डेटा',
    beyond_vault: 'पर्यवेक्षक के ऑप्टिकल मेहराब के परे',
    within_vault: 'पर्यवेक्षक के ऑप्टिकल मेहराब के भीतर',
    twilight_civil: 'नागरिक सांध्य', twilight_nautical: 'नौसैनिक सांध्य',
    twilight_astronomical: 'खगोलीय सांध्य',
    daylight: 'दिन का प्रकाश', night: 'रात',
    sun_never_leaves: '(सूर्य ऑप्टिकल मेहराब नहीं छोड़ता)',
    sun_never_enters: '(सूर्य ऑप्टिकल मेहराब में नहीं प्रवेश करता)',
    next_solar_eclipse: 'अगला सूर्य ग्रहण',
    next_lunar_eclipse: 'अगला चंद्र ग्रहण',
    about_btn: 'बारे में', legend_btn: 'विवरण',
    about_p1: 'यह एक संकल्पनात्मक मॉडल है। तारों की स्थिति विशुद्ध रूप से गतिकी पर आधारित है; पर्यवेक्षक का ऑप्टिकल मेहराब दृष्टि की सीमा को परिभाषित करता है जिस पर तारा क्षेत्र प्रक्षेपित होता है।',
    about_p2: 'अक्षांश/देशांतर ग्रेटिक्यूल का उपयोग करने वाला कोई भी संकल्पनात्मक मॉडल किसी अन्य प्रक्षेपण से न तो बेहतर है और न ही खराब। ग्रेटिक्यूल पृथ्वी के केंद्र पर एक काल्पनिक पर्यवेक्षक के लिए आंतरिक रूप से सुसंगत और स्व-संदर्भात्मक है — वही काल्पनिक पर्यवेक्षक 60 NMi / 1° संबंध को तारों की भू-स्थितियों से जोड़ता है।',
    about_p3: '"काल्पनिक पर्यवेक्षक" संकल्पनात्मक मॉडल के साथ परस्पर अनन्य नहीं है: ऐसा हर मॉडल अंततः इस पर आधारित होता है कि एक वास्तविक पर्यवेक्षक अपने दृष्टि अर्धगोले में क्या देखता है, और ग्रेटिक्यूल केवल उस पर एक बहीखाता परिपाटी है।',
  },
};

let _lang = 'en';
const _subs = new Set();

export function setLang(id) {
  if (!STRINGS[id]) return;
  if (_lang === id) return;
  _lang = id;
  for (const fn of _subs) { try { fn(); } catch (_) {} }
}

export function t(key) {
  return (STRINGS[_lang] && STRINGS[_lang][key])
      || STRINGS.en[key]
      || key;
}

export function onLangChange(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

export const LANGUAGES = [
  { id: 'en', label: 'EN' }, { id: 'cs', label: 'CZ' }, { id: 'es', label: 'ES' },
  { id: 'fr', label: 'FR' }, { id: 'de', label: 'DE' }, { id: 'it', label: 'IT' },
  { id: 'pt', label: 'PT' }, { id: 'pl', label: 'PL' }, { id: 'nl', label: 'NL' },
  { id: 'sk', label: 'SK' }, { id: 'ru', label: 'RU' }, { id: 'ar', label: 'AR' },
  { id: 'he', label: 'HE' }, { id: 'zh', label: 'ZH' }, { id: 'ja', label: 'JA' },
  { id: 'ko', label: 'KO' }, { id: 'th', label: 'TH' }, { id: 'hi', label: 'HI' },
];

const RTL_LANGS = new Set(['ar', 'he']);
export function isRtl(id) { return RTL_LANGS.has(id || _lang); }
